<template>
  <div :style="styleVars" class="h-full bg-muted">
    <!-- Subdomain Loading Overlay -->
    <div v-if="subdomainLoading" style="position: absolute; top: 0; left: 0; width: 100vw; height: 100vh; z-index: 9999; background: rgba(255,255,255,0.95); display: flex; align-items: center; justify-content: center;">
      <div class="text-center">
        <v-progress-circular indeterminate color="primary" size="48"></v-progress-circular>
        <p class="text-gray-600 mt-4 text-lg">Loading store locator...</p>
        <p class="text-gray-500 text-sm">Fetching configuration for {{ subdomainName }}</p>
      </div>
    </div>
    <!-- Main Content -->
    <div class="flex flex-col items-center justify-center w-full pb-20 bg-muted">
      <section class="flex flex-col items-center w-full justify-center">
        <!-- Header Section -->
        <div v-if="isLoading" class="flex items-center justify-between md:mt-5 mb-2 md:mb-0 mt-2 px-2 md:px-0 w-full header-bar">
          <!-- Skeleton Left Side: Page Title -->
          <div class="items-center gap-2 px-4 -ml-2 d-flex">
            <div class="skeleton-logo"></div>
            <div class="flex items-center gap-1">
              <div class="skeleton-brand-name"></div>
              <div class="skeleton-location-count"></div>
            </div>
          </div>

          <!-- Skeleton Header Tabs -->
          <div class="header-tabs flex gap-2 px-4 py-2">
            <div class="skeleton-tab"></div>
            <div class="skeleton-tab"></div>
            <div class="skeleton-tab"></div>
          </div>

          <!-- Skeleton Right Side: Search -->
          <div class="flex items-center ml-auto justify-end gap-2 mr-0 md:mr-6" style="max-width: 100%; min-width: 0;">
            <div class="skeleton-search"></div>
          </div>
        </div>

        <div v-else class="flex items-center justify-between md:mt-5 mb-2 md:mb-0 mt-2 px-2 md:px-0 w-full header-bar">
          <!-- Left Side: Page Title -->
          <div class="items-center gap-2 px-4 -ml-2 d-flex flex-shrink-0">
            <img
              v-if="storeLogo && storeLogo !== 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+'"
              :src="storeLogo"
              class="cursor-pointer object-contain dynamic-logo"
              :alt="brandName"
              @error="handleLogoError"
              @load="handleLogoLoad"
              @loadstart="() => console.log('🔄 [BRAND LOGO] Header logo loading started:', storeLogo)"
            />
            <v-icon
              v-else
              class="cursor-pointer dynamic-logo"
              color="primary"
              :size="logoIconSize"
            >
              mdi-store
            </v-icon>
            <div class="flex items-center gap-1 flex-wrap">
              <p class="brand-name-text font-bold">{{ brandName }}</p>
              <span class="text-sm ml-2 hidden sm:inline" v-if="totalLocations > 0" style="color: var(--store-brand-name-color);">
                ({{ totalLocations }} locations)
              </span>
            </div>
          </div>

          <!-- Right Side: Tabs, Search, and Mobile Location Button -->
          <div class="flex items-center gap-4 ml-auto">
            <!-- Header Tabs -->
            <div v-if="headerTabs.length && !isWidget" class="header-tabs flex gap-2 px-4 py-2 flex-wrap justify-center">
              <button
                v-for="tab in headerTabs"
                :key="tab.value"
                :class="['header-tab', { 'header-tab-selected': selectedTab === tab.value }]"
                @click="selectHeaderTab(tab.value)"
              >
                {{ tab.label }}
              </button>
            </div>

            <!-- Mobile Hamburger Menu Button -->
            <button
              v-if="isMobile && isSmallScreen"
              class="mobile-hamburger-btn details-btn px-3 py-2 font-semibold transition-colors flex items-center gap-2"
              @click="toggleMobileSidePanel"
            >
              <v-icon size="20">mdi-menu</v-icon>
              <span class="hidden sm:inline">Menu</span>
              <span v-if="totalLocations > 0" class="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">
                {{ totalLocations }}
              </span>
            </button>

            <!-- Search -->
            <div class="flex items-center justify-end gap-2 mr-0 md:mr-6 flex-1 min-w-0" v-if="!isMobile">
              <div class="relative w-full max-w-full">
                <v-text-field
                  v-model="searchQuery"
                  placeholder="Search locations..."
                  prepend-inner-icon="mdi-magnify"
                  outlined
                  dense
                  hide-details
                  class="search-input w-full"
                />
              </div>
            </div>

            <!-- Embed Widget Button -->
            <div v-if="!isWidget" class="mr-4 hidden sm:block">
              <embed-widget type="store-locator" />
            </div>
          </div>
        </div>

        <!-- Main Content -->
        <div class="w-full bg-white rounded-lg shadow-md">
          <!-- Skeleton Loader for Main Content -->
          <div v-if="isLoading && !locations.length" class="grid grid-cols-1 lg:grid-cols-3 gap-0 min-h-screen">
            <!-- Skeleton Tree View Panel -->
            <div class="lg:col-span-1 border-r border-gray-200">
              <!-- Skeleton City Filter -->
              <div class="city-filter-container mb-4 px-4">
                <div class="skeleton-label mb-1"></div>
                <div class="skeleton-dropdown"></div>
              </div>
              <div class="p-4 side-panel-tree-view h-full">
                <!-- Skeleton Tree Items -->
                <div class="space-y-4">
                  <!-- Country Level Skeleton -->
                  <div class="skeleton-tree-item">
                    <div class="skeleton-chevron"></div>
                    <div class="skeleton-icon"></div>
                    <div class="skeleton-text flex-1"></div>
                    <div class="skeleton-count"></div>
                  </div>
                  
                  <!-- State Level Skeleton -->
                  <div class="ml-6 space-y-3">
                    <div class="skeleton-tree-item">
                      <div class="skeleton-chevron"></div>
                      <div class="skeleton-icon"></div>
                      <div class="skeleton-text flex-1"></div>
                      <div class="skeleton-count"></div>
                    </div>
                    
                    <!-- Location Level Skeleton -->
                    <div class="ml-6 space-y-2">
                      <div class="skeleton-location-item"></div>
                      <div class="skeleton-location-item"></div>
                      <div class="skeleton-location-item"></div>
                    </div>
                  </div>
                  
                  <!-- More Country Items -->
                  <div class="skeleton-tree-item">
                    <div class="skeleton-chevron"></div>
                    <div class="skeleton-icon"></div>
                    <div class="skeleton-text flex-1"></div>
                    <div class="skeleton-count"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Skeleton Details Panel -->
            <div class="lg:col-span-2">
              <div class="p-6 h-full">
                <!-- Skeleton Cover Photo -->
                <div class="skeleton-cover-photo mb-6"></div>
                
                <!-- Skeleton Details Header -->
                <div class="skeleton-details-header mb-6"></div>
                
                <!-- Skeleton Content Sections -->
                <div class="space-y-6">
                  <!-- Skeleton Address Panel -->
                  <div class="skeleton-panel">
                    <div class="skeleton-panel-header"></div>
                    <div class="skeleton-panel-content">
                      <div class="skeleton-text-long"></div>
                      <div class="skeleton-button"></div>
                    </div>
                    <div class="skeleton-map"></div>
                  </div>
                  
                  <!-- Skeleton Contact Panel -->
                  <div class="skeleton-panel">
                    <div class="skeleton-panel-header"></div>
                    <div class="skeleton-panel-content">
                      <div class="space-y-4">
                        <div class="skeleton-contact-item"></div>
                        <div class="skeleton-contact-item"></div>
                        <div class="skeleton-contact-item"></div>
                      </div>
                    </div>
                  </div>
                  
                  <!-- Skeleton Description Panel -->
                  <div class="skeleton-panel">
                    <div class="skeleton-panel-header"></div>
                    <div class="skeleton-panel-content">
                      <div class="skeleton-text-long"></div>
                      <div class="skeleton-text-medium"></div>
                    </div>
                  </div>
                  
                  <!-- Skeleton Reviews Panel -->
                  <div class="skeleton-panel">
                    <div class="skeleton-panel-header"></div>
                    <div class="skeleton-panel-content">
                      <div class="space-y-4">
                        <div class="skeleton-review-item"></div>
                        <div class="skeleton-review-item"></div>
                        <div class="skeleton-review-item"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Error State (shown when no data is available) -->
          <div v-if="!isLoading && locations.length === 0" class="min-h-screen flex flex-col bg-white">
            <div class="flex-1 flex items-center justify-center px-6">
              <div class="text-center max-w-md">
                <div class="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <v-icon size="48" color="gray">mdi-map-marker-off</v-icon>
                </div>
                <h1 class="text-2xl font-bold text-gray-800 mb-4">No Locations Found</h1>
                <p class="text-gray-600 mb-6">
                  We couldn't find any store locations for this business. This might be due to a configuration issue or the locations may not be set up yet.
                </p>
                <button
                  class="details-btn px-6 py-3 font-semibold transition-colors"
                  @click="fetchLocations"
                >
                  <v-icon left size="20" class="mr-2">mdi-refresh</v-icon>
                  Try Again
                </button>
              </div>
            </div>
          </div>

          <!-- Actual Content -->
          <div v-else-if="!isLoading || locations.length > 0" class="grid grid-cols-1 lg:grid-cols-3 gap-0 min-h-screen">
            <!-- Tree View Panel - Hidden on Mobile and Small Desktop -->
            <div class="lg:col-span-1 border-r border-gray-200 hidden lg:block">
              <!-- City Filter Dropdown -->
              <div
                class="city-filter-container mb-4 px-4"
                style="--city-filter-primary: #000;"
              >
                <label for="city-filter" class="block text-sm font-semibold mb-1 city-filter-label">Filter by City:</label>
                <select
                  id="city-filter"
                  v-model="selectedCityFilter"
                  class="city-filter-dropdown w-full border rounded px-3 py-2 focus:outline-none"
                >
                  <option value="">All Cities</option>
                  <option v-for="city in uniqueCities" :key="city">{{ city }}</option>
                  <option value="__other__">Other Cities</option>
                </select>
              </div>
              <div class="p-4 side-panel-tree-view h-full">
                <div v-if="Object.keys(locationTree).length === 0" class="text-center py-8">
                  <v-icon large color="gray">mdi-map-marker-off</v-icon>
                  <p class="text-gray-500 mt-2">No locations found</p>
                </div>

                <div v-else>
                  <!-- Country Level -->
                  <div
                    v-for="(countryData, country) in filteredLocationTree"
                    :key="country"
                    class="mb-4"
                  >
                    <div
                      class="flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-50 rounded"
                      @click="toggleCountry(country)"
                    >
                      <v-icon small :class="['chevron-icon', expandedCountries.includes(country) ? 'rotate-90' : '']">mdi-chevron-right</v-icon>
                      <v-icon v-if="showSidepanelIcons === 'all'" small>mdi-earth</v-icon>
                      <span class="font-semibold text-gray-700">{{ (country || 'Other Countries').charAt(0).toUpperCase() + (country || 'Other Countries').slice(1) }}</span>
                      <span class="text-xs text-gray-500 ml-auto">({{ countryData.totalCount }})</span>
                    </div>

                    <!-- State Level -->
                    <div v-show="expandedCountries.includes(country)" class="ml-6 mt-2">
                      <div
                        v-for="(stateData, state) in countryData.states"
                        :key="`${country}-${state}`"
                        class="mb-2"
                      >
                        <div
                          class="flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-50 rounded"
                          @click="toggleState(country, state)"
                        >
                          <v-icon small :class="['chevron-icon', expandedStates.includes(`${country}-${state}`) ? 'rotate-90' : '']">mdi-chevron-right</v-icon>
                          <v-icon v-if="showSidepanelIcons === 'all'" small>mdi-map</v-icon>
                          <span class="font-medium text-gray-600">{{ state || 'Other States' }}</span>
                          <span class="text-xs text-gray-500 ml-auto">({{ stateData.locations.length }})</span>
                        </div>

                        <!-- Location Level -->
                        <div v-show="expandedStates.includes(`${country}-${state}`)" class="ml-6 mt-2">
                          <div
                            v-for="location in stateData.locations"
                            :key="location.id"
                            class="flex items-center gap-2 p-2 cursor-pointer rounded-xl transition-colors location-selector-item"
                            :class="{
                              'location-selected': selectedLocation?.id === location.id
                            }"
                            @click="selectLocation(location)"
                          >
                            <v-icon v-if="showSidepanelIcons === 'all'" small :color="location.iconColor || 'primary'">mdi-store</v-icon>
                            <div class="flex-1 min-w-0">
                              <p class="text-sm font-medium text-gray-800 truncate">
                                {{ location.name || location.storeLocation }}
                              </p>
                              <p class="text-xs text-gray-500 truncate">
                                {{ location.city }}{{ location.city && location.state ? ', ' : '' }}{{ location.state }}
                              </p>
                            </div>
                            <div class="flex-shrink-0">
                              <v-icon small class="chevron-icon">mdi-chevron-right</v-icon>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Details Panel -->
            <div class="lg:col-span-2 w-full">
              <div v-if="!selectedLocation" class="flex items-center justify-center h-full bg-gray-50">
                <div class="text-center">
                  <v-icon large color="gray">mdi-map-marker-outline</v-icon>
                  <p class="text-gray-500 mt-4 text-lg">Select a location to view details</p>
                  <p class="text-gray-400 text-sm">Choose a location from the tree view on the left</p>
                </div>
              </div>

              <div v-else>
                <div class="p-6 h-full">
                  <!-- Cover Photo at the very top -->
                  <div class="details-panel details-cover-photo mb-6" v-if="getCoverPhoto(selectedLocation) && !coverPhotoError">
                    <div class="cover-photo-container">
                      <div v-if="coverPhotoLoading" class="w-full h-64 bg-gray-200 rounded-lg shadow-md flex items-center justify-center">
                        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                        <span class="ml-2 text-gray-500">Loading cover photo...</span>
                      </div>
                      <img 
                        v-show="!coverPhotoLoading"
                        :src="getCoverPhoto(selectedLocation)" 
                        :alt="`Cover photo for ${selectedLocation.name || selectedLocation.storeLocation}`"
                        class="w-full h-64 object-cover rounded-lg shadow-md"
                        @error="handleCoverPhotoError"
                        @load="handleCoverPhotoLoad"
                        @loadstart="coverPhotoLoading = true"
                      />
                    </div>
                  </div>

                  <!-- Details Header -->
                  <div class="details-header mb-6 shadow-lg p-8">
                    <div class="flex flex-row items-center justify-between w-full">
                      <div class="flex flex-col items-start flex-1 min-w-0">
                        <h2 class="text-2xl font-bold text-gray-800 mb-2 flex items-center">
                          <v-icon class="mr-2 details-header-icon">mdi-store</v-icon>
                          {{ selectedLocation.name || selectedLocation.storeLocation }}
                        </h2>
                        <div class="flex flex-col gap-2 text-lg font-medium text-gray-700 mb-2 w-full">
                          <div v-if="selectedLocation.averageRating" class="flex items-center gap-1">
                            <span class="text-lg font-semibold mr-2">{{ selectedLocation.averageRating.toFixed(1) }}</span>
                            <span class="text-gray-500 text-base mr-2" v-if="selectedLocation.reviewCount">
                              ({{ selectedLocation.reviewCount }})
                            </span>
                            <span class="star-rating flex items-center">
                              <v-icon
                                v-for="star in 5"
                                :key="'star-'+star"
                                :color="star <= fullStars ? 'amber' : (star === fullStars + 1 && decimalPart >= 0.5 ? 'amber' : 'grey-lighten-2')"
                                size="22"
                                class="star-icon"
                              >
                                {{ star <= fullStars ? 'mdi-star' : (star === fullStars + 1 && decimalPart >= 0.5 ? 'mdi-star-half-full' : 'mdi-star-outline') }}
                              </v-icon>
                            </span>
                          </div>
                          <div v-if="selectedLocation.gmbProfile && selectedLocation.gmbProfile.categories && selectedLocation.gmbProfile.categories.primaryCategory && selectedLocation.gmbProfile.categories.primaryCategory.displayName"
                               class="flex items-center gap-1">
                            <v-icon class="details-header-icon" size="20" :style="{ color: 'var(--details-primary)' }">mdi-tag</v-icon>
                            <span class="details-btn px-2 py-1 rounded-full text-sm font-semibold">
                              {{ selectedLocation.gmbProfile.categories.primaryCategory.displayName }}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div class="flex-shrink-0 ml-6">
                        <img
                          v-if="selectedLocationLogo && selectedLocationLogo !== 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+'"
                          :src="selectedLocationLogo"
                          class="object-contain rounded-lg shadow-md details-header-logo"
                          :alt="brandName + ' logo'"
                          style="max-width: 120px; max-height: 120px;"
                          @error="handleLogoError"
                          @load="handleLogoLoad"
                          @loadstart="() => console.log('🔄 [STORE LOGO] Details header logo loading started:', selectedLocationLogo)"
                        />
                        <v-icon
                          v-else
                          class="details-header-logo"
                          color="primary"
                          :size="logoIconSize"
                        >
                          mdi-store
                        </v-icon>
                      </div>
                    </div>
                  </div>

                  <div class="space-y-6">
                    <!-- Address Panel -->
                    <div class="details-panel details-address flex flex-col md:flex-row gap-8 items-stretch mb-6">
                      <div class="flex-1 flex-col items-start">
                        <h3 class="text-xl font-extrabold text-gray-800 mb-1 flex items-center tracking-tight address-header">
                          <v-icon class="mr-2 details-header-icon">mdi-map-marker</v-icon>
                          Address
                        </h3>
                        <p class="text-gray-900 text-lg font-semibold leading-relaxed mt-0">{{ getFullAddress(selectedLocation) }}</p>
                        <button
                          v-if="selectedLocation && (apifyLat && apifyLng || getFullAddress(selectedLocation))"
                          class="details-btn mt-4 px-4 py-2 font-semibold transition-colors"
                          @click="getDirections"
                        >
                          <v-icon left size="20" class="mr-1">mdi-directions</v-icon>
                          Get Directions
                        </button>
                      </div>
                      <div class="w-full md:w-1/2 h-56 md:h-48 rounded-xl overflow-hidden flex-shrink-0 shadow-md border border-blue-100">
                        <iframe
                          v-if="selectedLocation && ((apifyLat && apifyLng) || selectedLocation.place_id || getFullAddress(selectedLocation))"
                          :src="getGoogleMapsSrc()"
                          width="100%"
                          height="100%"
                          style="border:0;"
                          allowfullscreen=""
                          loading="lazy"
                          referrerpolicy="no-referrer-when-downgrade"
                        ></iframe>
                        <div v-else class="flex items-center justify-center h-full bg-gray-100 text-gray-400">
                          No map available
                        </div>
                      </div>
                    </div>

                    <!-- Contact & Web Panel -->
                    <div class="details-panel details-contact flex flex-col md:flex-row gap-8 items-stretch mb-6">
                      <div class="flex-1 flex flex-col justify-center">
                        <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                          <v-icon class="mr-2 details-header-icon">mdi-phone</v-icon>
                          Contact & Web
                        </h3>
                        <div class="space-y-4">
                          <div v-if="selectedLocation.phone">
                            <label class="text-sm font-medium text-gray-500">Phone</label>
                            <p class="text-gray-800">
                              <a :href="`tel:${selectedLocation.phone}`" class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-150">
                                {{ selectedLocation.phone }}
                              </a>
                              <button
                                class="details-btn ml-2 px-3 py-1 font-semibold transition-colors text-sm"
                                @click="callBusiness"
                              >
                                <v-icon left size="18" class="mr-1">mdi-phone</v-icon>
                                Call Now
                              </button>
                            </p>
                          </div>
                          <div v-if="selectedLocation.email">
                            <label class="text-sm font-medium text-gray-500">Email</label>
                            <p class="text-gray-800">
                              <a :href="`mailto:${selectedLocation.email}`" class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-150">
                                {{ selectedLocation.email }}
                              </a>
                            </p>
                          </div>
                          <div v-if="selectedLocation.website">
                            <label class="text-sm font-medium text-gray-500">Website</label>
                            <p class="text-gray-800">
                              <a :href="selectedLocation.website" target="_blank" class="text-blue-600 hover:text-blue-800 hover:underline transition-colors duration-150">
                                {{ formatWebsite(selectedLocation.website) }}
                              </a>
                              <button
                                class="details-btn ml-2 px-3 py-1 font-semibold transition-colors text-sm"
                                @click="visitWebsite"
                              >
                                <v-icon left size="18" class="mr-1">mdi-web</v-icon>
                                Visit Website
                              </button>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Business Description -->
                    <div class="details-panel details-description mb-6" v-if="getBusinessDescription(selectedLocation)">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-text</v-icon>
                        About This Business
                      </h3>
                      <p class="text-gray-700 leading-relaxed text-lg">{{ getBusinessDescription(selectedLocation) }}</p>
                    </div>

                    <!-- Products & Services -->
                    <div class="details-panel details-products mb-6" v-if="availableServices.length > 0">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-cog</v-icon>
                        {{ getProductsServicesSectionName() }}
                      </h3>
                      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div
                          v-for="(service, index) in availableServices"
                          :key="index"
                          class="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-100"
                        >
                          <div
                            v-if="service.image"
                            class="w-full h-32 mb-4 overflow-hidden rounded-lg"
                          >
                            <img
                              :src="service.image"
                              :alt="service.name"
                              class="w-full h-full object-cover"
                            />
                          </div>
                          <div
                            v-else
                            class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4"
                          >
                            <v-icon color="blue" size="24">mdi-cog</v-icon>
                          </div>
                          <h4 class="text-lg font-semibold text-gray-800 mb-2">
                            {{ service.name }}
                          </h4>
                          <p
                            v-if="service.description"
                            class="text-gray-600 text-sm leading-relaxed mb-3"
                          >
                            {{ service.description }}
                          </p>
                          <div
                            v-if="service.price"
                            class="text-base font-semibold text-blue-600"
                          >
                            {{ service.price }}
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Opening Hours -->
                    <div class="details-panel details-hours mb-6" v-if="getOpeningHours(selectedLocation).length > 0">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-clock</v-icon>
                        Business Hours
                      </h3>
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div
                          v-for="(dayHours, index) in getOpeningHours(selectedLocation)"
                          :key="index"
                          class="flex justify-between items-center p-3 rounded-lg bg-white/80 border border-blue-100 mb-2"
                          :class="isCurrentDay(dayHours.day) ? 'current-day' : ''"
                        >
                          <span class="font-medium text-gray-700">{{ dayHours.day }}</span>
                          <span class="text-gray-600">{{ dayHours.hours || 'Closed' }}</span>
                        </div>
                      </div>
                    </div>

                    <!-- Categories & Services -->
                    <!-- Remove the following block from the details panel:
                    <div class="details-panel details-categories mb-6">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-tag-multiple</v-icon>
                        Categories & Services
                      </h3>
                      <div class="space-y-4">
                        <div v-if="getPrimaryCategory(selectedLocation)">
                          <label class="text-sm font-medium text-gray-500">Primary Category</label>
                          <p class="text-gray-800 font-semibold text-lg">{{ getPrimaryCategory(selectedLocation) }}</p>
                        </div>
                        <div v-if="getAdditionalCategories(selectedLocation).length > 0">
                          <label class="text-sm font-medium text-gray-500">Additional Categories</label>
                          <div class="flex flex-wrap gap-2 mt-2">
                            <span
                              v-for="category in getAdditionalCategories(selectedLocation)"
                              :key="category"
                              class="details-btn px-2 py-1 rounded-full text-sm font-semibold mr-2 mb-2 inline-block"
                            >
                              {{ category }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    -->

                    <!-- Reviews Panel -->
                    <div class="details-panel details-reviews mb-6">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-star</v-icon>
                        Google Reviews
                      </h3>
                      <div v-if="isReviewsLoading" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                        <p class="text-gray-500 mt-2">Loading reviews...</p>
                      </div>
                      <div v-else-if="reviews.length === 0" class="text-gray-500 py-4">No reviews found for this location.</div>
                      <div v-else class="space-y-4">
                        <div v-for="review in paginatedReviews" :key="review.reviewId" class="review-item">
                          <div class="flex items-center gap-3 mb-2">
                            <img
                              :src="review.profilePhotoUrl || 'https://www.gravatar.com/avatar/?d=mp'"
                              alt="profile"
                              class="w-8 h-8 rounded-full object-cover"
                              @error="event => event.target.src = 'https://www.gravatar.com/avatar/?d=mp'"
                            />
                            <div>
                              <div class="review-author">{{ review.displayName }}</div>
                              <div class="flex items-center gap-2">
                                <div class="flex">
                                  <span v-for="index in 5" :key="index">
                                    <svg :class="index <= review.starRating ? 'text-yellow-400' : 'text-gray-300'" class="w-3 h-3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                    </svg>
                                  </span>
                                </div>
                                <div class="review-date">{{ formatDate(review.updateTime) }}</div>
                              </div>
                            </div>
                          </div>
                          <div class="review-comment">{{ review.comment }}</div>
                        </div>
                      </div>
                      
                      <!-- Pagination Controls -->
                      <div v-if="reviews.length > reviewsPerPage" class="pagination-container">
                        <div class="pagination-info">
                          Showing {{ (reviewsPage - 1) * reviewsPerPage + 1 }} to {{ Math.min(reviewsPage * reviewsPerPage, reviews.length) }} of {{ reviews.length }} reviews
                        </div>
                        <div class="flex items-center gap-2">
                          <button
                            @click="prevReviewPage"
                            :disabled="!hasPrevReviewPage"
                            class="details-btn px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <v-icon left size="16">mdi-chevron-left</v-icon>
                            Previous
                          </button>
                          <span class="pagination-page-info">
                            Page {{ reviewsPage }} of {{ totalReviewPages }}
                          </span>
                          <button
                            @click="nextReviewPage"
                            :disabled="!hasNextReviewPage"
                            class="details-btn px-3 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                            <v-icon right size="16">mdi-chevron-right</v-icon>
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- Posts Panel -->
                    <div class="details-panel details-posts mb-6">
                      <h3 class="text-xl font-extrabold text-gray-800 mb-3 flex items-center tracking-tight">
                        <v-icon class="mr-2 details-header-icon">mdi-post</v-icon>
                        Recent Posts
                      </h3>
                      <div v-if="isPostsLoading" class="text-center py-4">
                        <v-progress-circular indeterminate color="primary" size="32"></v-progress-circular>
                        <p class="text-gray-500 mt-2">Loading posts...</p>
                      </div>
                      <div v-else-if="posts.length === 0" class="text-gray-500 py-4">No posts found for this location.</div>
                      <div v-else class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 items-start post-grid">
                        <div v-for="post in posts" :key="post.id" class="bg-gray-50 rounded-lg p-4 shadow-sm border border-gray-100 flex flex-col items-start post-card cursor-pointer hover:shadow-md transition-shadow duration-200" @click="openPostModal(post)">
                          <!-- Date Only -->
                          <div class="flex items-center gap-2 mb-3">
                            <span class="text-xs text-gray-500">
                              {{ formatDate(post.created_at?.seconds ? post.created_at.seconds * 1000 : post.created_at) }}
                            </span>
                          </div>
                          
                          <!-- Images on Top - Responsive Size -->
                          <div v-if="post.images && post.images.length > 0" class="mb-4 w-full">
                            <div class="grid grid-cols-1 gap-2 w-full">
                              <div class="flex justify-center w-full">
                                <img
                                  v-for="(image, index) in post.images.slice(0, 1)"
                                  :key="index"
                                  :src="image"
                                  alt="post image"
                                  class="w-full h-32 md:h-40 object-cover bg-gray-100 rounded-lg shadow-md"
                                />
                              </div>
                              <div v-if="post.images.length > 1" class="grid grid-cols-2 gap-1 w-full">
                                <div class="flex justify-center w-full">
                                  <img
                                    v-for="(image, index) in post.images.slice(1, 2)"
                                    :key="index"
                                    :src="image"
                                    alt="post image"
                                    class="w-full h-16 md:h-20 object-cover bg-gray-100 rounded-lg shadow-sm"
                                  />
                                </div>
                                <div class="flex justify-center w-full">
                                  <img
                                    v-for="(image, index) in post.images.slice(2, 3)"
                                    :key="index"
                                    :src="image"
                                    alt="post image"
                                    class="w-full h-16 md:h-20 object-cover bg-gray-100 rounded-lg shadow-sm"
                                  />
                                </div>
                                <div v-if="post.images.length > 3" class="h-16 md:h-20 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500">
                                  +{{ post.images.length - 3 }}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <!-- Videos on Top - Responsive Size -->
                          <div v-if="post.videos && post.videos.length > 0" class="mb-4">
                            <div class="grid grid-cols-1 gap-2">
                              <div
                                v-for="(video, index) in post.videos.slice(0, 1)"
                                :key="index"
                                class="w-full h-32 md:h-40 bg-gray-200 rounded-lg flex items-center justify-center shadow-md relative"
                              >
                                <v-icon size="32" color="white" class="absolute">mdi-play-circle</v-icon>
                                <div class="absolute bottom-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                                  Video
                                </div>
                              </div>
                              <div v-if="post.videos.length > 1" class="grid grid-cols-2 gap-1">
                                <div
                                  v-for="(video, index) in post.videos.slice(1, 3)"
                                  :key="index"
                                  class="h-16 md:h-20 bg-gray-200 rounded-lg flex items-center justify-center relative"
                                >
                                  <v-icon size="16" color="white">mdi-play</v-icon>
                                </div>
                                <div v-if="post.videos.length > 3" class="h-16 md:h-20 bg-gray-200 rounded-lg flex items-center justify-center text-xs text-gray-500">
                                  +{{ post.videos.length - 3 }}
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <!-- Post Title -->
                          <div v-if="post.name" class="text-base font-semibold text-gray-800 mb-2">
                            {{ post.name }}
                          </div>
                          
                          <!-- Post Text at Bottom -->
                          <div class="text-gray-700 text-sm leading-relaxed flex-grow w-full">
                            <span v-if="post.text">
                              {{ (post.text.length > 150 && !expandedPosts[post.id]) ? post.text.substring(0, 150) + '...' : post.text }}
                            </span>
                            <span 
                              v-if="post.text && post.text.length > 150" 
                              @click.stop="toggleReadMore(post.id)" 
                              class="text-blue-500 hover:underline ml-1 font-semibold text-xs cursor-pointer"
                            >
                              {{ expandedPosts[post.id] ? 'Show less' : 'Show more' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Action Buttons -->

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
    
    <!-- Fixed Left Menu Button for Small Desktop -->
    <button
      v-if="isMobile && !isSmallScreen"
      class="fixed-left-menu-btn"
      @click="toggleMobileSidePanel"
    >
      <v-icon size="24">mdi-menu</v-icon>
      <span class="menu-label">Menu</span>
    </button>

    <!-- Mobile Side Panel -->
    <div
      v-if="isMobile && showMobileSidePanel"
      class="mobile-side-panel-overlay"
      @click="closeMobileSidePanel"
    >
      <div
        class="mobile-side-panel"
        @click.stop
      >
        <!-- Header -->
        <div class="mobile-side-panel-header">
          <div class="mobile-side-panel-title">
            <v-icon size="24" class="mr-2">mdi-map-marker-multiple</v-icon>
            <span class="font-semibold text-lg">Locations</span>
            <span v-if="totalLocations > 0" class="text-sm text-gray-500 ml-2">({{ totalLocations }})</span>
          </div>
          <button
            class="mobile-side-panel-close"
            @click="closeMobileSidePanel"
          >
            <v-icon size="24">mdi-close</v-icon>
          </button>
        </div>

        <!-- City Filter -->
        <div class="mobile-side-panel-filter">
          <label for="mobile-side-panel-city-filter" class="block text-sm font-semibold mb-2 text-gray-700">Filter by City:</label>
          <select
            id="mobile-side-panel-city-filter"
            v-model="selectedCityFilter"
            class="mobile-side-panel-city-dropdown w-full border-2 border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
          >
            <option value="">All Cities</option>
            <option v-for="city in uniqueCities" :key="city">{{ city }}</option>
            <option value="__other__">Other Cities</option>
          </select>
        </div>

        <!-- Location List -->
        <div class="mobile-side-panel-list">
          <div v-if="Object.keys(locationTree).length === 0" class="text-center py-8">
            <v-icon large color="gray">mdi-map-marker-off</v-icon>
            <p class="text-gray-500 mt-2">No locations found</p>
          </div>

          <div v-else class="space-y-2">
            <!-- Country Level -->
            <div
              v-for="(countryData, country) in filteredLocationTree"
              :key="country"
              class="mobile-side-panel-country-section"
            >
              <div
                class="mobile-side-panel-country-header"
                @click="toggleCountry(country)"
              >
                <v-icon small :class="['chevron-icon', expandedCountries.includes(country) ? 'rotate-90' : '']">mdi-chevron-right</v-icon>
                <v-icon v-if="showSidepanelIcons === 'all'" small class="mr-2">mdi-earth</v-icon>
                <span class="font-semibold text-gray-700 flex-1">{{ (country || 'Other Countries').charAt(0).toUpperCase() + (country || 'Other Countries').slice(1) }}</span>
                <span class="text-xs text-gray-500">({{ countryData.totalCount }})</span>
              </div>

              <!-- State Level -->
              <div v-show="expandedCountries.includes(country)" class="mobile-side-panel-state-section">
                <div
                  v-for="(stateData, state) in countryData.states"
                  :key="`${country}-${state}`"
                  class="mobile-side-panel-state-item"
                >
                  <div
                    class="mobile-side-panel-state-header"
                    @click="toggleState(country, state)"
                  >
                    <v-icon small :class="['chevron-icon', expandedStates.includes(`${country}-${state}`) ? 'rotate-90' : '']">mdi-chevron-right</v-icon>
                    <v-icon v-if="showSidepanelIcons === 'all'" small class="mr-2">mdi-map</v-icon>
                    <span class="font-medium text-gray-600 flex-1">{{ state || 'Other States' }}</span>
                    <span class="text-xs text-gray-500">({{ stateData.locations.length }})</span>
                  </div>

                  <!-- Location Level -->
                  <div v-show="expandedStates.includes(`${country}-${state}`)" class="mobile-side-panel-location-section">
                    <div
                      v-for="location in stateData.locations"
                      :key="location.id"
                      class="mobile-side-panel-location-item"
                      :class="{
                        'mobile-side-panel-location-selected': selectedLocation?.id === location.id
                      }"
                      @click="selectLocationAndClose(location)"
                    >
                      <v-icon v-if="showSidepanelIcons === 'all'" small :color="location.iconColor || 'primary'" class="mr-3">mdi-store</v-icon>
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-medium text-gray-800 truncate">
                          {{ location.name || location.storeLocation }}
                        </p>
                        <p class="text-xs text-gray-500 truncate">
                          {{ location.city }}{{ location.city && location.state ? ', ' : '' }}{{ location.state }}
                        </p>
                      </div>
                      <v-icon small class="chevron-icon">mdi-chevron-right</v-icon>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Footer Section -->
    <footer class="store-footer">
      <div class="footer-links" v-if="footerCities.length > 0">
        <template v-for="(city, idx) in footerCities">
          <a
            :key="city.city"
            href="#"
            class="footer-link"
            @click.prevent="selectedCityFilter = city.city === 'Unknown' ? '__other__' : city.city"
          >
            {{ brandName }} in {{ city.city === 'Unknown' ? 'Other Locations' : city.city }}
          </a>
          <span v-if="idx !== footerCities.length - 1" class="footer-separator hidden sm:inline"> | </span>
        </template>
      </div>
      <div class="footer-powered-by">
        <p class="text-xs text-gray-500 mt-2">
          <a href="https://fly-social.com" target="_blank" class="hover:underline" style="color: inherit;">Powered by Fly</a>
        </p>
      </div>
    </footer>

    <!-- Post Modal -->
    <div v-if="showPostModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click="closePostModal">
      <div class="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" @click.stop>
        <!-- Modal Header with Posted Time -->
        <div class="flex items-center justify-between p-6 border-b border-gray-200">
          <div class="flex items-center gap-2">
            <span class="text-sm text-gray-500">
              {{ formatDate(selectedPost?.created_at?.seconds ? selectedPost.created_at.seconds * 1000 : selectedPost?.created_at) }}
            </span>
          </div>
          <button @click="closePostModal" class="text-gray-500 hover:text-gray-700 bg-transparent !bg-transparent border-none outline-none focus:outline-none focus:ring-0" style="background-color: transparent !important;">
            <v-icon size="24" class="text-gray-500 hover:text-gray-700">mdi-close</v-icon>
          </button>
        </div>

        <!-- Modal Content -->
        <div class="p-6">

          <!-- Images -->
          <div v-if="selectedPost?.images && selectedPost.images.length > 0" class="mb-6">
            <div class="grid grid-cols-1 gap-4">
              <div v-for="(image, index) in selectedPost.images" :key="index" class="bg-gray-100 rounded-lg p-4">
                <img
                  :src="image"
                  :alt="`Post image ${index + 1}`"
                  class="w-full object-contain rounded-lg"
                />
              </div>
            </div>
          </div>

          <!-- Videos -->
          <div v-if="selectedPost?.videos && selectedPost.videos.length > 0" class="mb-6">
            <div class="grid grid-cols-1 gap-4">
              <div
                v-for="(video, index) in selectedPost.videos"
                :key="index"
                class="w-full h-64 bg-gray-200 rounded-lg flex items-center justify-center shadow-md relative"
              >
                <v-icon size="48" color="white" class="absolute">mdi-play-circle</v-icon>
                <div class="absolute bottom-4 right-4 bg-black bg-opacity-50 text-white text-sm px-3 py-1 rounded">
                  Video
                </div>
              </div>
            </div>
          </div>

          <!-- Post Title -->
          <div v-if="selectedPost?.name" class="text-xl font-bold text-gray-800 mb-4">
            {{ selectedPost.name }}
          </div>

          <!-- Post Text -->
          <div v-if="selectedPost?.text" class="text-gray-700 leading-relaxed text-base">
            {{ selectedPost.text }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { auth, db, colUsers, platforms } from "@/utils/firebase.utils";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import PostCard from '@/components/post/PostCard.vue';
import { 
  updateMetaTags, 
  generateStoreLocatorTitle, 
  generateStoreLocatorDescription, 
  generateStoreLocatorStructuredData, 
  injectStructuredData, 
  cleanupMetaTags 
} from "@/utils/seo.utils";
import { isValidSubdomain, extractSubdomainName, getCurrentEnvironment, getDomainConfig } from "@/utils/domainConfig";
export default {
  components: {
    PostCard
  },
  props: {
    embedUid: {
      type: String,
      default: null
    },
    embedWid: {
      type: String,
      default: null
    },
    embedSlug: {
      type: String,
      default: null
    },
    storeLocatorStyles: {
      type: Object,
      default: () => ({})
    },
    disableNavigation: {
      type: Boolean,
      default: false
    },
    disableFavicon: {
      type: Boolean,
      default: false
    },
    disablePageTitle: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      isLoading: false,
      locations: [],
      locationTree: {},
      selectedLocation: null,
      searchQuery: "",
      expandedCountries: [],
      expandedStates: [],
      platformLogoCache: {},
      workspaceData: null,
      gmbSettingsData: null, // Add GMB settings data
      apifyLat: null,
      apifyLng: null,
      _originalFaviconHref: null,
      _originalPageTitle: null,
      logoLoadError: false,
      logoLoaded: false,
      selectedTab: null,
      selectedCityFilter: "",
      // Add review state
      reviews: [],
      isReviewsLoading: false,
      // Add pagination state for reviews
      reviewsPage: 1,
      reviewsPerPage: 5,
      totalReviews: 0,
      // Add posts state
      posts: [],
      isPostsLoading: false,
      expandedPosts: {},
      coverPhotoError: false,
      coverPhotoLoading: false,
      showLocationList: false,
      showMobileSidePanel: false,
      // NEW: Make isMobile reactive - show hamburger menu when side panel would be hidden
      isMobile: typeof window !== 'undefined' ? window.innerWidth <= 1024 : false,
      // Track if we're on a small screen (mobile/tablet)
      isSmallScreen: typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
      // Add subdomain data
      subdomainData: null,
      subdomainLoading: false,
      // Add platforms data for posts
      allPlatforms: [],
      // Add widget key data
      widgetKey: null,
      widgetData: null,
      isWidgetLoading: false,
      // Post modal data
      showPostModal: false,
      selectedPost: null,
    };
  },
  computed: {
    currentUser() {
      return this.$store.getters.currentUser;
    },
    authUser() {
      return this.$store.getters.authUser;
    },
    // Widget detection computed properties
    isWidget() {
      return this.$route.name === "store-locator-widget";
    },
    isSubdomain() {
      const hostname = window.location.hostname;
      const currentEnv = getCurrentEnvironment();
      const isSubdomain = isValidSubdomain(hostname, 'stores');
      console.log("🔍 Subdomain detection:", {
        hostname,
        currentEnv,
        isSubdomain,
        expectedSuffix: this.getDomainConfig?.subdomainSuffix,
      });
      return isSubdomain;
    },
    subdomainName() {
      if (!this.isSubdomain) {
        console.log("🔍 No subdomain name - not on subdomain");
        return null;
      }
      const hostname = window.location.hostname;
      const subdomainName = extractSubdomainName(hostname, 'stores');
      console.log("🔍 Subdomain name extracted:", subdomainName);
      return subdomainName;
    },
    WID() {
      console.log("🔍 WID resolution:", {
        isSubdomain: this.isSubdomain,
        hasSubdomainData: !!this.subdomainData,
        isWidget: this.isWidget,
        embedWid: this.embedWid,
        storeWID: this.$store.getters.WID,
        widgetKey: this.$route.params.id
      });
      
      // For widget, use the wid from widgetData (fetched from Firebase)
      if (this.isWidget && this.widgetData) {
        console.log("✅ Using widget WID:", this.widgetData.wid);
        return this.widgetData.wid;
      }
      
      // For subdomain, use the wid from subdomainData
      if (this.isSubdomain && this.subdomainData) {
        console.log("✅ Using subdomain WID:", this.subdomainData.wid);
        return this.subdomainData.wid;
      }
      
      // For regular embed, use embedWid prop
      const wid = this.embedWid || this.$store.getters.WID;
      console.log("✅ Using regular WID:", wid);
      return wid;
    },
    currentUID() {
      console.log("🔍 UID resolution:", {
        isSubdomain: this.isSubdomain,
        hasSubdomainData: !!this.subdomainData,
        isWidget: this.isWidget,
        hasWidgetData: !!this.widgetData,
        embedUid: this.embedUid,
        storeUID: this.$store.getters.currentUID,
        widgetData: this.widgetData
      });
      
      // For widget, use the uid from widgetData (fetched from Firebase)
      if (this.isWidget && this.widgetData) {
        console.log("✅ Using widget UID:", this.widgetData.uid);
        return this.widgetData.uid;
      }
      
      // For subdomain, use the uid from subdomainData
      if (this.isSubdomain && this.subdomainData) {
        console.log("✅ Using subdomain UID:", this.subdomainData.uid);
        return this.subdomainData.uid;
      }
      
      // Use embed UID from URL path
      if (this.embedUid) {
        console.log("✅ Using embed UID from URL:", this.embedUid);
        return this.embedUid;
      }
      
      // Fall back to store UID
      const storeUid = this.$store.getters.currentUID;
      console.log("✅ Using store UID:", storeUid);
      return storeUid;
    },
    globalStores() {
      return this.$store.getters.globalStores || [];
    },
    mergedStoreLocatorStyles() {
      // Merge prop with workspace data, prop takes precedence
      const workspaceStyles = this.workspaceData?.store_locator_styles || {};
      return { ...workspaceStyles, ...this.storeLocatorStyles };
    },
    totalLocations() {
      return this.locations.length;
    },
    uniqueCities() {
      // Get unique city names from all locations
      const cities = new Set();
      this.locations.forEach(loc => {
        if (loc.city) cities.add(loc.city);
      });
      return Array.from(cities).sort();
    },
    filteredLocationTree() {
      // If city filter is applied, filter locations by city
      let filteredTree = this.locationTree;
      if (this.selectedCityFilter) {
        const filtered = {};
        Object.entries(this.locationTree).forEach(([country, countryData]) => {
          const filteredStates = {};
          let countryHasResults = false;
          Object.entries(countryData.states).forEach(([state, stateData]) => {
            let matchingLocations;
            if (this.selectedCityFilter === "__other__") {
              // Show locations with no city
              matchingLocations = stateData.locations.filter(
                location => !location.city
              );
            } else {
              // Show locations matching the selected city
              matchingLocations = stateData.locations.filter(
                location => location.city === this.selectedCityFilter
              );
            }
            if (matchingLocations.length > 0) {
              filteredStates[state] = {
                ...stateData,
                locations: matchingLocations
              };
              countryHasResults = true;
            }
          });
          if (countryHasResults) {
            filtered[country] = {
              ...countryData,
              states: filteredStates,
              totalCount: Object.values(filteredStates).reduce((sum, state) => sum + state.locations.length, 0)
            };
          }
        });
        filteredTree = filtered;
      }
      // Apply search filter if present
      if (!this.searchQuery.trim()) {
        return filteredTree;
      }
      const query = this.searchQuery.toLowerCase();
      const filtered = {};
      Object.entries(filteredTree).forEach(([country, countryData]) => {
        const filteredStates = {};
        let countryHasResults = false;
        Object.entries(countryData.states).forEach(([state, stateData]) => {
          const matchingLocations = stateData.locations.filter(location => {
            const searchableText = [
              location.name,
              location.storeLocation,
              location.city,
              location.state,
              location.country,
              location.zipcode,
              location.sub_locality
            ].filter(Boolean).join(' ').toLowerCase();
            return searchableText.includes(query);
          });
          if (matchingLocations.length > 0) {
            filteredStates[state] = {
              ...stateData,
              locations: matchingLocations
            };
            countryHasResults = true;
          }
        });
        if (countryHasResults) {
          filtered[country] = {
            ...countryData,
            states: filteredStates,
            totalCount: Object.values(filteredStates).reduce((sum, state) => sum + state.locations.length, 0)
          };
        }
      });
      return filtered;
    },
    isEmbed() {
      return !!this.embedUid;
    },
    storeLogo() {
      if (!this.workspaceData) {
        console.log("🚫 [BRAND LOGO] No workspace data available for logo");
        return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+';
      }
      
      // Check multiple possible logo fields in order of preference
      const logo = this.workspaceData.logo_original || 
                   this.workspaceData.logo_url || 
                   this.workspaceData.logo_dark || 
                   this.workspaceData.logo_transparent ||
                   this.workspaceData.logo;
      
      console.log("🏢 [BRAND LOGO] Workspace data:", this.workspaceData);
      console.log("🔍 [BRAND LOGO] Available logo fields:", {
        logo_original: this.workspaceData.logo_original,
        logo_url: this.workspaceData.logo_url,
        logo_dark: this.workspaceData.logo_dark,
        logo_transparent: this.workspaceData.logo_transparent,
        logo: this.workspaceData.logo
      });
      console.log("✅ [BRAND LOGO] Selected logo URL:", logo);
      console.log("📊 [BRAND LOGO] Logo URL type:", typeof logo);
      console.log("🔗 [BRAND LOGO] Logo URL length:", logo ? logo.length : 0);
      
      return logo || 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+';
    },
    brandName() {
      if (!this.workspaceData) {
        console.log("No workspace data available for brand name");
        return 'Loading...';
      }
      const name = this.workspaceData.name;
      console.log("Brand name:", name);
      return name || 'Store Brand';
    },
    fullStars() {
      if (!this.selectedLocation || !this.selectedLocation.averageRating) return 0;
      return Math.floor(this.selectedLocation.averageRating);
    },
    decimalPart() {
      if (!this.selectedLocation || !this.selectedLocation.averageRating) return 0;
      return this.selectedLocation.averageRating - this.fullStars;
    },
    styleVars() {
      const headerStyles = this.mergedStoreLocatorStyles.header_styles || {};
      const sidepanelStyles = this.mergedStoreLocatorStyles.sidepanel_styles || {};
      const detailsStyles = this.mergedStoreLocatorStyles.details_styles || {};
      const common = detailsStyles.common || {};
      const button = (detailsStyles.button || {});
      function getBtnVar(key, fallback) {
        return button[key] || fallback;
      }
      function getCommonVar(key, fallback) {
        return common[key] || fallback;
      }
      return {
        '--store-primary': this.mergedStoreLocatorStyles.primaryColor || '#1773EA',
        '--store-secondary': this.mergedStoreLocatorStyles.secondaryColor || '#6B7280',
        '--store-panel-bg': this.mergedStoreLocatorStyles.panelBg || 'linear-gradient(90deg, #eff6ff 0%, #e0e7ff 100%)',
        '--store-header-text': this.mergedStoreLocatorStyles.headerTextColor || '#222',
        '--store-body-text': this.mergedStoreLocatorStyles.bodyTextColor || '#333',
        '--store-border-radius': this.mergedStoreLocatorStyles.borderRadius || '1.25rem',
        '--store-font-family': this.mergedStoreLocatorStyles.fontFamily || 'Inter, sans-serif',
        '--store-button-bg': this.mergedStoreLocatorStyles.buttonBg || '#1773EA',
        '--store-button-text': this.mergedStoreLocatorStyles.buttonTextColor || '#fff',
        '--store-selected-bg': this.mergedStoreLocatorStyles.selectedBg || 'rgba(23, 115, 234, 0.1)',
        '--store-selected-border': this.mergedStoreLocatorStyles.selectedBorder || '#1773EA',
        '--store-selected-text': this.mergedStoreLocatorStyles.selectedText || '#1773EA',
        '--store-logo-size-mobile': headerStyles.logoSizeMobile || '2.25rem',
        '--store-logo-size-desktop': headerStyles.logoSizeDesktop || '3rem',
        '--store-header-bg': headerStyles.headerBg || '#fff',
        '--store-tab-text': headerStyles.tabTextColor || '#333',
        '--store-tab-hover-text': headerStyles.tabHoverTextColor || '#1773EA',
        '--store-tab-selected-text': headerStyles.tabSelectedTextColor || '#fff',
        '--store-tab-bg': headerStyles.tabBg || '#f3f4f6',
        '--store-tab-hover-bg': headerStyles.tabHoverBg || '#e0e7ff',
        '--store-tab-selected-bg': headerStyles.tabSelectedBg || '#1773EA',
        '--store-search-border': headerStyles.searchBorderColor || '#bdb4e7',
        '--store-search-radius': headerStyles.searchBorderRadius || '0.75rem',
        '--store-search-border-size': headerStyles.searchBorderSize || '2px',
        '--store-brand-name-size': headerStyles.brandNameSize || '1.375rem',
        '--store-brand-name-color': headerStyles.brandNameColor || '#000000',
        '--sidepanel-selected-bg': sidepanelStyles.selectedBg || 'rgba(23, 115, 234, 0.15)',
        '--sidepanel-selected-text': sidepanelStyles.selectedText || 'rgba(23, 115, 234, 1)',
        '--sidepanel-selected-border': sidepanelStyles.selectedBorder || 'rgba(23, 115, 234, 1)',
        '--sidepanel-selected-radius': sidepanelStyles.selectedRadius || '1.25rem',
        '--sidepanel-hover-bg': sidepanelStyles.hoverBg || 'rgba(23, 115, 234, 0.08)',
        '--sidepanel-hover-text': sidepanelStyles.hoverText || 'rgba(23, 115, 234, 1)',
        '--sidepanel-hover-border': sidepanelStyles.hoverBorder || 'rgba(23, 115, 234, 1)',
        '--sidepanel-hover-border-size': sidepanelStyles.hoverBorderSize || '2px',
        '--sidepanel-hover-radius': sidepanelStyles.hoverRadius || '1.25rem',
        '--sidepanel-unselected-bg': sidepanelStyles.unselectedBg || '#fff',
        '--sidepanel-unselected-text': sidepanelStyles.unselectedText || '#222',
        '--sidepanel-unselected-border': sidepanelStyles.unselectedBorder || 'transparent',
        '--sidepanel-unselected-border-size': sidepanelStyles.unselectedBorderSize || '1px',
        '--sidepanel-unselected-radius': sidepanelStyles.unselectedRadius || '0.75rem',
        '--sidepanel-icon': sidepanelStyles.iconColor || 'rgba(23, 115, 234, 1)',
        '--sidepanel-chevron': sidepanelStyles.chevronColor || 'rgba(23, 115, 234, 0.7)',
        '--sidepanel-selected-border-size': sidepanelStyles.selectedBorderSize || '2px',
        '--details-address-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-address-text': getCommonVar('textColor', '#222'),
        '--details-address-border': getCommonVar('borderColor', '#1773EA'),
        '--details-address-border-size': getCommonVar('borderSize', '2px'),
        '--details-address-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-contact-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-contact-text': getCommonVar('textColor', '#222'),
        '--details-contact-border': getCommonVar('borderColor', '#1773EA'),
        '--details-contact-border-size': getCommonVar('borderSize', '2px'),
        '--details-contact-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-description-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-description-text': getCommonVar('textColor', '#222'),
        '--details-description-border': getCommonVar('borderColor', '#1773EA'),
        '--details-description-border-size': getCommonVar('borderSize', '2px'),
        '--details-description-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-cover-photo-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-cover-photo-text': getCommonVar('textColor', '#222'),
        '--details-cover-photo-border': getCommonVar('borderColor', '#1773EA'),
        '--details-cover-photo-border-size': getCommonVar('borderSize', '2px'),
        '--details-cover-photo-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-social-links-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-social-links-text': getCommonVar('textColor', '#222'),
        '--details-social-links-border': getCommonVar('borderColor', '#1773EA'),
        '--details-social-links-border-size': getCommonVar('borderSize', '2px'),
        '--details-social-links-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-hours-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-hours-text': getCommonVar('textColor', '#222'),
        '--details-hours-border': getCommonVar('borderColor', '#1773EA'),
        '--details-hours-border-size': getCommonVar('borderSize', '2px'),
        '--details-hours-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-categories-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-categories-text': getCommonVar('textColor', '#222'),
        '--details-categories-border': getCommonVar('borderColor', '#1773EA'),
        '--details-categories-border-size': getCommonVar('borderSize', '2px'),
        '--details-categories-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-posts-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-posts-text': getCommonVar('textColor', '#222'),
        '--details-posts-border': getCommonVar('borderColor', '#1773EA'),
        '--details-posts-border-size': getCommonVar('borderSize', '2px'),
        '--details-posts-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-actions-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-actions-text': getCommonVar('textColor', '#222'),
        '--details-actions-border': getCommonVar('borderColor', '#1773EA'),
        '--details-actions-border-size': getCommonVar('borderSize', '2px'),
        '--details-actions-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-btn-text': getBtnVar('textColor', '#fff'),
        '--details-btn-bg': getBtnVar('bg', '#1773EA'),
        '--details-btn-border': getBtnVar('borderColor', '#1773EA'),
        '--details-btn-radius': getBtnVar('borderRadius', '0.75rem'),
        '--details-btn-border-size': getBtnVar('borderSize', '1.5px'),
        '--details-btn-hover-text': getBtnVar('hoverTextColor', '#fff'),
        '--details-btn-hover-bg': getBtnVar('hoverBg', '#125bb5'),
        '--details-btn-hover-border': getBtnVar('hoverBorderColor', '#125bb5'),
        '--details-btn-hover-border-size': getBtnVar('hoverBorderSize', '1.5px'),
        '--details-btn-hover-border-radius': getBtnVar('hoverBorderRadius', '0.75rem'),
        '--details-btn-selected-text': getBtnVar('selectedTextColor', '#fff'),
        '--details-btn-selected-bg': getBtnVar('selectedBg', '#125bb5'),
        '--details-btn-selected-border': getBtnVar('selectedBorderColor', '#125bb5'),
        '--details-btn-selected-border-size': getBtnVar('selectedBorderSize', '1.5px'),
        '--details-btn-selected-border-radius': getBtnVar('selectedBorderRadius', '0.75rem'),
        '--details-header-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-header-text': getCommonVar('textColor', '#222'),
        '--details-header-border': getCommonVar('borderColor', '#1773EA'),
        '--details-header-border-size': getCommonVar('borderSize', '2px'),
        '--details-header-radius': getCommonVar('borderRadius', '1.25rem'),
        // Basic color scheme for details panel
        '--details-primary': getCommonVar('primary', '#1976d2'),
        '--details-secondary': getCommonVar('secondary', '#424242'),
        '--details-accent': getCommonVar('accent', '#82B1FF'),
        '--details-info': getCommonVar('info', '#2196F3'),
        '--details-success': getCommonVar('success', '#4CAF50'),
        '--details-warning': getCommonVar('warning', '#FFC107'),
        '--details-error': getCommonVar('error', '#FF5252'),
        '--details-posts-border-size': getCommonVar('borderSize', '2px'),
        '--details-posts-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-actions-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-actions-text': getCommonVar('textColor', '#222'),
        '--details-actions-border': getCommonVar('borderColor', '#1773EA'),
        '--details-actions-border-size': getCommonVar('borderSize', '2px'),
        '--details-actions-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-reviews-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-reviews-text': getCommonVar('textColor', '#222'),
        '--details-reviews-border': getCommonVar('borderColor', '#1773EA'),
        '--details-reviews-border-size': getCommonVar('borderSize', '2px'),
        '--details-reviews-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-products-bg': getCommonVar('bg', '#f0f0f0'),
        '--details-products-text': getCommonVar('textColor', '#222'),
        '--details-products-border': getCommonVar('borderColor', '#1773EA'),
        '--details-products-border-size': getCommonVar('borderSize', '2px'),
        '--details-products-radius': getCommonVar('borderRadius', '1.25rem'),
        '--details-btn-text': getBtnVar('textColor', '#fff'),
      };
    },
    logoIconSize() {
      // Convert the mobile logo size to a number for the icon size
      const mobileSize = this.mergedStoreLocatorStyles.logoSizeMobile || '2.25rem';
      const desktopSize = this.mergedStoreLocatorStyles.logoSizeDesktop || '3rem';
      
      // Extract the numeric value and convert to a reasonable icon size
      const mobileNum = parseInt(mobileSize);
      const desktopNum = parseInt(desktopSize);
      
      // Use the larger size for the icon, but cap it at a reasonable maximum
      const iconSize = Math.min(Math.max(mobileNum, desktopNum), 64);
      
      return iconSize.toString();
    },
    headerTabs() {
      const tabs = this.mergedStoreLocatorStyles.headerTabs;
      if (Array.isArray(tabs) && tabs.length > 0) {
        // Update the first tab to use website URL if it's the Home tab
        if (tabs[0] && tabs[0].label === 'Home') {
          const websiteUrl = this.getWebsiteUrl();
          return [{ label: 'Home', value: websiteUrl || 'home' }];
        }
        return tabs;
      }
      // Default to Home tab with website URL
      const websiteUrl = this.getWebsiteUrl();
      return [{ label: 'Home', value: websiteUrl || 'home' }];
    },
    footerCities() {
      // Collect unique cities with at least one store from all locations (not filtered)
      const seen = new Set();
      const cities = [];
      Object.values(this.locationTree).forEach(countryData => {
        Object.values(countryData.states).forEach(stateData => {
          stateData.locations.forEach(location => {
            const city = location.city || location.state || 'Unknown';
            if (!seen.has(city)) {
              seen.add(city);
              cities.push({
                city,
                location
              });
            }
          });
        });
      });
      return cities;
    },
    showSidepanelIcons() {
      // Use string: 'all' (default) or 'none'
      const val = this.mergedStoreLocatorStyles.sidepanel_styles && this.mergedStoreLocatorStyles.sidepanel_styles.showIcons;
      return (typeof val === 'string' ? val : 'all');
    },
    // Add computed properties for review pagination
    paginatedReviews() {
      const startIndex = (this.reviewsPage - 1) * this.reviewsPerPage;
      const endIndex = startIndex + this.reviewsPerPage;
      return this.reviews.slice(startIndex, endIndex);
    },
    totalReviewPages() {
      return Math.ceil(this.reviews.length / this.reviewsPerPage);
    },
    hasNextReviewPage() {
      return this.reviewsPage < this.totalReviewPages;
    },
    hasPrevReviewPage() {
      return this.reviewsPage > 1;
    },
    availableServices() {
      // Get services from GMB settings data (highest priority - this is where BusinessDetails.vue saves data)
      if (
        this.gmbSettingsData?.productsAndServices &&
        Array.isArray(this.gmbSettingsData.productsAndServices)
      ) {
        const filtered = this.gmbSettingsData.productsAndServices.filter(
          (service) => service && service.name && service.name.trim() !== ""
        );
        return filtered;
      }

      // Get services from selected location data
      if (!this.selectedLocation) {
        return [];
      }

      // First priority: individual store configuration product_and_services
      if (
        this.selectedLocation.product_and_services &&
        Array.isArray(this.selectedLocation.product_and_services)
      ) {
        const filtered = this.selectedLocation.product_and_services.filter(
          (service) => service && service.name && service.name.trim() !== ""
        );
        return filtered;
      }

      // Second priority: productsAndServices in the location data
      if (
        this.selectedLocation.productsAndServices &&
        Array.isArray(this.selectedLocation.productsAndServices)
      ) {
        const filtered = this.selectedLocation.productsAndServices.filter(
          (service) => service && service.name && service.name.trim() !== ""
        );
        return filtered;
      }

      // Check for services in GMB profile data
      if (
        this.selectedLocation.gmbProfile?.productsAndServices &&
        Array.isArray(this.selectedLocation.gmbProfile.productsAndServices)
      ) {
        const filtered =
          this.selectedLocation.gmbProfile.productsAndServices.filter(
            (service) => service && service.name && service.name.trim() !== ""
          );
        return filtered;
      }

      // Check for services in shallow profile data
      if (
        this.selectedLocation.gmb_shallow_profile_data?.productsAndServices &&
        Array.isArray(
          this.selectedLocation.gmb_shallow_profile_data.productsAndServices
        )
      ) {
        const filtered =
          this.selectedLocation.gmb_shallow_profile_data.productsAndServices.filter(
            (service) => service && service.name && service.name.trim() !== ""
          );
        return filtered;
      }

      // Check for services in workspace data (lowest priority)
      if (
        this.workspaceData?.gmb_details?.productsAndServices &&
        Array.isArray(this.workspaceData.gmb_details.productsAndServices)
      ) {
        const filtered =
          this.workspaceData.gmb_details.productsAndServices.filter(
            (service) => service && service.name && service.name.trim() !== ""
          );
        return filtered;
      }

      return [];
    },
    // New computed property for individual store logos
    selectedLocationLogo() {
      if (!this.selectedLocation) {
        console.log("🚫 [STORE LOGO] No selected location for logo");
        return null;
      }

      // Check if the location has GMB profile data with media items
      if (this.selectedLocation.gmbProfile && 
          this.selectedLocation.gmbProfile.mediaItems && 
          this.selectedLocation.gmbProfile.mediaItems.logo) {
        
        const logoData = this.selectedLocation.gmbProfile.mediaItems.logo;
        console.log("🏪 [STORE LOGO] Found GMB logo data:", logoData);
        
        // Use thumbnailUrl as preferred, fallback to sourceUrl
        const logoUrl = logoData.thumbnailUrl || logoData.sourceUrl;
        
        if (logoUrl) {
          console.log("✅ [STORE LOGO] Using individual store logo:", logoUrl);
          return logoUrl;
        }
      }

      // Fallback to workspace logo if no individual store logo found
      console.log("🔄 [STORE LOGO] No individual store logo found, falling back to workspace logo");
      const fallbackLogo = this.storeLogo;
      return fallbackLogo && fallbackLogo !== 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+' ? fallbackLogo : null;
    },
  },
  watch: {
    selectedLocation: {
      immediate: true,
      handler(newVal) {
        console.log('[selectedLocation watcher] Location changed:', newVal);
        console.log('[selectedLocation watcher] New location ID:', newVal?.id);
        console.log('[selectedLocation watcher] New location name:', newVal?.name || newVal?.storeLocation);
        
        // Reset cover photo error when location changes
        this.coverPhotoError = false;
        this.coverPhotoLoading = false;
        
        if (newVal && newVal.id) {
          console.log('[selectedLocation watcher] Fetching data for location:', newVal.id);
          this.fetchApifyLatLng(newVal.id);
          this.fetchReviewsForLocation(newVal);
          this.fetchPostsForLocation(newVal);
        } else {
          console.log('[selectedLocation watcher] Clearing location data');
          this.apifyLat = null;
          this.apifyLng = null;
          this.reviews = [];
          this.posts = [];
          // Reset pagination when clearing location
          this.reviewsPage = 1;
          // Reset expanded posts state when clearing location
          this.expandedPosts = {};
        }
      }
    },
    workspaceData: {
      immediate: true,
      handler(newVal) {
        if (newVal) {
          console.log("Workspace data updated, updating favicon...");
          this.updateFavicon();
        }
      }
    },
    storeLogo: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.workspaceData) {
          console.log("Store logo updated, updating favicon...", newVal);
          this.updateFavicon();
        }
      }
    },
    brandName: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal !== 'Loading...') {
          console.log("Brand name updated, updating page title...", newVal);
          this.updatePageTitle();
        }
      }
    },
    headerTabs: {
      immediate: true,
      handler(newTabs) {
        if (newTabs && newTabs.length && !this.selectedTab) {
          this.selectedTab = newTabs[0].value;
        }
      }
    },
    // Watch for slug changes in the route to support browser navigation
    '$route.params.slug': {
      immediate: false,
      handler(newSlug) {
        if (!newSlug || !this.locations || this.locations.length === 0) return;
        const slugify = str => str && str.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const match = this.locations.find(loc => slugify(loc.name || loc.storeLocation) === newSlug);
        if (match && (!this.selectedLocation || this.selectedLocation.id !== match.id)) {
          this.selectedLocation = match;
          // Expand treeview for selected location
          const country = match.country || 'Other Countries';
          const state = match.state || 'Other States';
          if (!this.expandedCountries.includes(country)) {
            this.expandedCountries.push(country);
          }
          const stateKey = `${country}-${state}`;
          if (!this.expandedStates.includes(stateKey)) {
            this.expandedStates.push(stateKey);
          }
        }
      }
    },
    selectedCityFilter: {
      immediate: false,
      handler(newCity) {
        if (!newCity) return; // If filter is cleared, do nothing
        // Get all locations in the filtered city
        let filteredLocations = [];
        if (newCity === "__other__") {
          filteredLocations = this.locations.filter(loc => !loc.city);
        } else {
          filteredLocations = this.locations.filter(loc => loc.city === newCity);
        }
        // If the current selectedLocation is in the filtered city, do nothing
        if (
          this.selectedLocation &&
          ((newCity === "__other__" && !this.selectedLocation.city) ||
           (this.selectedLocation.city === newCity))
        ) {
          return;
        }
        // Otherwise, select the first location in the filtered city (if any)
        if (filteredLocations.length > 0) {
          const firstLoc = filteredLocations[0];
          this.selectedLocation = firstLoc;
          // Expand treeview for selected location
          const country = firstLoc.country || 'Other Countries';
          const state = firstLoc.state || 'Other States';
          if (!this.expandedCountries.includes(country)) {
            this.expandedCountries.push(country);
          }
          const stateKey = `${country}-${state}`;
          if (!this.expandedStates.includes(stateKey)) {
            this.expandedStates.push(stateKey);
          }
        }
      }
    },
    // Watch for embedSlug prop changes
    embedSlug: {
      immediate: false,
      handler(newSlug) {
        if (newSlug && this.locations && this.locations.length > 0) {
          this.selectLocationBySlug(newSlug);
        }
      }
    },
    // Watch for reviews changes to reset pagination
    reviews: {
      handler(newReviews) {
        // Reset to first page when reviews change
        this.reviewsPage = 1;
      }
    },
    
    // Watch for workspace data changes to update SEO
    workspaceData: {
      handler(newWorkspaceData) {
        if (newWorkspaceData && this.locations.length > 0) {
          console.log('[StoreLocator] Workspace data changed, updating SEO');
          this.updateSEO();
        }
      },
      deep: true
    },
    
    // Watch for locations changes to update SEO
    locations: {
      handler(newLocations) {
        if (newLocations.length > 0 && this.workspaceData) {
          console.log('[StoreLocator] Locations changed, updating SEO');
          this.updateSEO();
        }
      },
      deep: true
    },
    
    // Watch for isLoading changes to debug skeleton loading
    isLoading: {
      handler(newVal, oldVal) {
        console.log('[StoreLocator] isLoading changed from', oldVal, 'to', newVal);
        console.log('[StoreLocator] Current locations count:', this.locations.length);
        console.log('[StoreLocator] Current workspaceData:', !!this.workspaceData);
      }
    },
  },
  methods: {
    getDomainConfig() {
      return getDomainConfig();
    },
    selectLocationBySlug(slug) {
      console.log('[selectLocationBySlug] Selecting location by slug:', slug);
      const slugify = str => str && str.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
      const match = this.locations.find(loc => slugify(loc.name || loc.storeLocation) === slug);
      if (match) {
        console.log('[selectLocationBySlug] Found matching location:', match);
        this.selectedLocation = match;
        // Expand treeview for selected location
        const country = match.country || 'Other Countries';
        const state = match.state || 'Other States';
        if (!this.expandedCountries.includes(country)) {
          this.expandedCountries.push(country);
        }
        const stateKey = `${country}-${state}`;
        if (!this.expandedStates.includes(stateKey)) {
          this.expandedStates.push(stateKey);
        }
      } else {
        console.log('[selectLocationBySlug] No matching location found for slug:', slug);
      }
    },

    async getAllPlatforms() {
      if (this.allPlatforms && this.allPlatforms.length != 0) return this.allPlatforms;
      const platformsRef = await getDocs(platforms);

      let allPlatforms = [];

      platformsRef.forEach((doc) => {
        allPlatforms.push(doc.data());
      });

      allPlatforms.sort((a, b) => a.display_priority - b.display_priority);

      this.allPlatforms = allPlatforms;

      return allPlatforms;
    },

    async fetchSubdomainData() {
      console.log("=== SUBDOMAIN DEBUGGING START ===");
      console.log("Current hostname:", window.location.hostname);
      console.log("Is subdomain:", this.isSubdomain);
      console.log("Subdomain name:", this.subdomainName);
      console.log("Firebase db instance:", db);
      console.log("Firebase project ID:", db.app.options.projectId);
      console.log("Firebase config:", db.app.options);
      
      if (!this.isSubdomain || !this.subdomainName) {
        console.log("❌ Not on a subdomain, skipping subdomain data fetch");
        console.log("=== SUBDOMAIN DEBUGGING END ===");
        return;
      }

      this.subdomainLoading = true;
      console.log("🔄 Fetching subdomain data for:", this.subdomainName);

      try {
        // Query the unique_domain collection with the subdomain name
        const domainRef = doc(db, "unique_domain", this.subdomainName);
        console.log("📄 Firebase document reference:", domainRef);
        console.log("📄 Document path:", domainRef.path);
        console.log("📄 Document ID:", domainRef.id);
        console.log("📄 Collection path:", domainRef.parent.path);
        
        console.log("⏳ Attempting to fetch document...");
        const startTime = Date.now();
        const docSnap = await getDoc(domainRef);
        const endTime = Date.now();
        console.log("✅ Document fetch completed in", endTime - startTime, "ms");
        console.log("📄 Document snapshot exists:", docSnap.exists());
        console.log("📄 Document snapshot metadata:", docSnap.metadata);

        if (docSnap.exists()) {
          const data = docSnap.data();
          console.log("✅ Subdomain data fetched successfully:", data);
          console.log("📊 Data keys:", Object.keys(data));
          console.log("📊 UID present:", !!data.uid);
          console.log("📊 WID present:", !!data.wid);
          console.log("📊 UID value:", data.uid);
          console.log("📊 WID value:", data.wid);
          
          // Validate that uid and wid are present
          if (data.uid && data.wid) {
            this.subdomainData = data;
            console.log("✅ Subdomain data set successfully");
            console.log("✅ UID:", data.uid);
            console.log("✅ WID:", data.wid);
          } else {
            console.error("❌ Subdomain data missing uid or wid");
            console.error("❌ Available data:", data);
            console.error("❌ UID type:", typeof data.uid);
            console.error("❌ WID type:", typeof data.wid);
            throw new Error("Invalid subdomain configuration - missing uid or wid");
          }
        } else {
          console.error("❌ Subdomain not found in unique_domain collection");
          console.error("❌ Subdomain name:", this.subdomainName);
          console.error("❌ Collection path:", "unique_domain");
          console.error("❌ Document ID:", this.subdomainName);
          throw new Error(`Subdomain '${this.subdomainName}' not found in unique_domain collection`);
        }
      } catch (error) {
        console.error("=== SUBDOMAIN ERROR DETAILS ===");
        console.error("❌ Error fetching subdomain data:", error);
        console.error("❌ Error name:", error.name);
        console.error("❌ Error message:", error.message);
        console.error("❌ Error code:", error.code);
        console.error("❌ Error stack:", error.stack);
        
        // Check if it's a Firebase connection error
        if (error.code === 'unavailable' || error.message.includes('offline')) {
          console.error("❌ Firebase connection issue detected");
          console.error("❌ This appears to be a network/Firebase connectivity problem");
          console.error("❌ Check your internet connection and Firebase configuration");
        }
        
        // Check if it's a permissions error
        if (error.code === 'permission-denied') {
          console.error("❌ Firebase permissions issue detected");
          console.error("❌ Check your Firebase security rules for the unique_domain collection");
        }
        
        // Check if it's a not-found error
        if (error.code === 'not-found') {
          console.error("❌ Document not found in Firebase");
          console.error("❌ Ensure the document exists in the unique_domain collection");
        }
        
        console.error("=== SUBDOMAIN ERROR END ===");
        
        // Show user-friendly error message
        if (this.$snackbar) {
          this.$snackbar.show(`Subdomain error: ${error.message}`);
        } else {
          console.error("❌ Snackbar not available for error display");
        }
      } finally {
        this.subdomainLoading = false;
        console.log("🔄 Subdomain loading state set to false");
        console.log("=== SUBDOMAIN DEBUGGING END ===");
      }
    },

    async fetchWorkspaceData() {
      if (!this.WID) {
        console.error("Cannot fetch workspace data: WID is empty");
        return;
      }
      if (!this.currentUID) {
        console.error("Cannot fetch workspace data: currentUID is empty");
        return;
      }
      
      console.log("Fetching workspace data for WID:", this.WID, "UID:", this.currentUID);
      
      try {
        // First try to get from global stores
        const workspaceFromStores = this.globalStores.find(store => store.id === this.WID);
        if (workspaceFromStores) {
          console.log("Found workspace in global stores:", workspaceFromStores);
          this.workspaceData = workspaceFromStores;
          return;
        }

        // If not in global stores, try Firestore
        const workspaceRef = doc(db, "users", this.currentUID, "fly_workspaces", this.WID);
        console.log("Fetching from Firestore path:", workspaceRef.path);
        
        const workspaceDoc = await getDoc(workspaceRef);
        
        if (workspaceDoc.exists()) {
          const data = workspaceDoc.data();
          console.log("Workspace data fetched from Firestore:", data);
          this.workspaceData = data;
        } else {
          console.error("Workspace not found in Firestore for WID:", this.WID);
          // Try to get from any available stores as last resort
          const allStores = [...this.globalStores, ...this.locations];
          const workspaceFromAnyStore = allStores.find(store => store.id === this.WID);
          if (workspaceFromAnyStore) {
            console.log("Found workspace in available stores:", workspaceFromAnyStore);
            this.workspaceData = workspaceFromAnyStore;
          }
        }
      } catch (error) {
        console.error("Error fetching workspace data:", error);
        // Try to get from any available stores as last resort
        const allStores = [...this.globalStores, ...this.locations];
        const workspaceFromAnyStore = allStores.find(store => store.id === this.WID);
        if (workspaceFromAnyStore) {
          console.log("Found workspace in available stores after error:", workspaceFromAnyStore);
          this.workspaceData = workspaceFromAnyStore;
          this.storeLocatorStyles = workspaceFromAnyStore.store_locator_styles || {};
        }
      }
    },

    // Widget-specific initialization
    async initializeWidget() {
      console.log('🔧 [StoreLocator] Initializing as widget');
      
      // Widget-specific setup
      this.isLoading = true;
      
      try {
        // Fetch widget data first
        await this.fetchWidgetData();
        
        // Fetch workspace data and locations for widget
        await this.fetchLocations();
        
        // Handle slug-based selection for widget
        if (this.embedSlug && !this.selectedLocation && this.locations && this.locations.length > 0) {
          this.selectLocationBySlug(this.embedSlug);
        }
        
        // Auto-select first location if none selected
        if (!this.selectedLocation && this.locations && this.locations.length > 0) {
          this.selectedLocation = this.locations[0];
          console.log('🔧 [StoreLocator] Auto-selected first location for widget:', this.selectedLocation);
        }
        
        // Update SEO for widget
        this.updateSEO();
        
      } catch (error) {
        console.error('🔧 [StoreLocator] Error initializing widget:', error);
      } finally {
        this.isLoading = false;
        console.log('🔧 [StoreLocator] Widget initialization completed');
      }
    },

    async fetchGMBSettingsData() {
      try {
        console.log('[fetchGMBSettingsData] Starting to fetch GMB settings...');
        
        const gmbSettingsCollectionRef = collection(
          db,
          "users",
          this.currentUID,
          "fly_workspaces",
          this.WID,
          "gmb-settings"
        );
        const latestDocQuery = query(
          gmbSettingsCollectionRef,
          orderBy("created_at", "desc"),
          limit(1)
        );
        const querySnapshot = await getDocs(latestDocQuery);

        if (!querySnapshot.empty) {
          const latestDoc = querySnapshot.docs[0];
          const gmbData = latestDoc.data().gmb_details;
          console.log('[fetchGMBSettingsData] GMB data found:', gmbData);
          
          this.gmbSettingsData = gmbData;
          
          if (gmbData.productsAndServices) {
            console.log('[fetchGMBSettingsData] Products and services found:', gmbData.productsAndServices);
          } else {
            console.log('[fetchGMBSettingsData] No products and services found in GMB data');
          }
        } else {
          console.log('[fetchGMBSettingsData] No GMB settings documents found');
          this.gmbSettingsData = null;
        }
      } catch (error) {
        console.error('[fetchGMBSettingsData] Error fetching GMB settings:', error);
        this.gmbSettingsData = null;
      }
    },

    async fetchLocations() {
      console.log('[fetchLocations] Starting fetch with isLoading:', this.isLoading);
      console.log('[fetchLocations] Current UID:', this.currentUID);
      console.log('[fetchLocations] Current WID:', this.WID);
      
      this.isLoading = true;
      console.log('[fetchLocations] Set isLoading to true');
      
      try {
        // Use global stores from Vuex if available
        if (this.globalStores && this.globalStores.length > 0) {
          console.log("Using globalStores from Vuex:", this.globalStores.length);
          // Try to get workspace data from global stores first
          const workspaceFromStores = this.globalStores.find(store => store.id === this.WID);
          if (workspaceFromStores) {
            console.log("Using workspace data from global stores:", workspaceFromStores);
            this.workspaceData = workspaceFromStores;
            this.storeLocatorStyles = workspaceFromStores.store_locator_styles || {};
          } else {
            // If not found in global stores, fetch from Firestore
            await this.fetchWorkspaceData();
          }
          // Fetch GMB settings data
          await this.fetchGMBSettingsData();
          this.processStores(this.globalStores);
        } else {
          // Fallback: fetch directly from Firestore
          console.log("Fetching locations directly from Firestore");
          if (!this.currentUID || !this.WID) {
            console.error("Cannot fetch locations: missing UID or WID");
            console.error("currentUID:", this.currentUID);
            console.error("WID:", this.WID);
            // Set empty data and continue
            this.locations = [];
            this.locationTree = {};
            return;
          }

          const storesRef = collection(
            db,
            "users",
            this.currentUID,
            "fly_workspaces"
          );
          const storesQuery = query(
            storesRef,
            where("admin", "==", this.WID),
            where("storeWorkspace", "==", true)
          );
          const storesSnapshot = await getDocs(storesQuery);

          const stores = storesSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));

          if (stores.length > 0) {
            console.log("Fetched stores from Firestore:", stores.length);
            // Update the Vuex store
            this.$store.commit("SET_GLOBAL_STORES", stores);
            // Try to get workspace data from fetched stores
            const workspaceFromStores = stores.find(store => store.id === this.WID);
            if (workspaceFromStores) {
              console.log("Using workspace data from fetched stores:", workspaceFromStores);
              this.workspaceData = workspaceFromStores;
              this.storeLocatorStyles = workspaceFromStores.store_locator_styles || {};
            } else {
              // If not found in fetched stores, fetch from Firestore
              await this.fetchWorkspaceData();
            }
            // Fetch GMB settings data
            await this.fetchGMBSettingsData();
            this.processStores(stores);
          } else {
            console.log("No stores found in Firestore");
            this.locations = [];
            this.locationTree = {};
            // Try to fetch workspace data as last resort
            await this.fetchWorkspaceData();
            // Fetch GMB settings data
            await this.fetchGMBSettingsData();
          }
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
        this.locations = [];
        this.locationTree = {};
      } finally {
        console.log('[fetchLocations] Setting isLoading to false');
        this.isLoading = false;
        console.log('[fetchLocations] Final isLoading state:', this.isLoading);
        console.log('[fetchLocations] Final locations count:', this.locations.length);
      }
    },

    processStores(stores) {
      console.log("Processing stores for tree view:", stores.length);
      
      // Process store data similar to Location.vue
      const locations = stores.map((storeData) => {
        let locationData = { ...storeData };
        locationData.phone = locationData?.gmb_shallow_profile_data
          ? locationData?.gmb_shallow_profile_data?.phone
          : locationData?.gmbProfile?.phoneNumbers?.primaryPhone;
        locationData.website = locationData?.gmb_shallow_profile_data
          ? locationData?.gmb_shallow_profile_data?.website
          : locationData?.gmbProfile?.websiteUri;
        locationData.averageRating = locationData?.gmb_shallow_profile_data
          ? locationData?.gmb_shallow_profile_data?.rating
          : locationData.averageRating || 0;
        locationData.reviewCount = locationData?.gmb_shallow_profile_data
          ? locationData?.gmb_shallow_profile_data?.reviews
          : locationData.reviewCount || 0;
        locationData.profile_strength = this.calculateProfileStrength(locationData);
        locationData.name = locationData?.gmb_shallow_profile_data
          ? locationData?.gmb_shallow_profile_data?.title
          : locationData?.gmbProfile?.title || locationData.name || "";

        // Add google_business_profile to connected_apps if GMB data exists
        if (
          (locationData.gmb_shallow_profile_data || locationData.gmbProfile) &&
          (!locationData.connected_apps ||
            !locationData.connected_apps.includes("google_business_profile"))
        ) {
          if (!locationData.connected_apps) {
            locationData.connected_apps = [];
          }
          if (!locationData.connected_apps.includes("google_business_profile")) {
            locationData.connected_apps.push("google_business_profile");
          }
        }

        return locationData;
      });

      this.locations = locations;
      this.buildLocationTree(locations);
      this.fetchAllPlatformLogos();
    },

    buildLocationTree(locations) {
      const tree = {};

      locations.forEach(location => {
        const country = location.country || 'Other Countries';
        const state = location.state || 'Other States';

        if (!tree[country]) {
          tree[country] = {
            states: {},
            totalCount: 0
          };
        }

        if (!tree[country].states[state]) {
          tree[country].states[state] = {
            locations: []
          };
        }

        tree[country].states[state].locations.push(location);
        tree[country].totalCount++;
      });

      // Sort locations within each state by name
      Object.values(tree).forEach(countryData => {
        Object.values(countryData.states).forEach(stateData => {
          stateData.locations.sort((a, b) => {
            const nameA = (a.name || a.storeLocation || '').toLowerCase();
            const nameB = (b.name || b.storeLocation || '').toLowerCase();
            return nameA.localeCompare(nameB);
          });
        });
      });

      this.locationTree = tree;
    },

    calculateProfileStrength(location) {
      const gmbProfile = location.gmbProfile;
      const shallowProfile = location.gmb_shallow_profile_data;
      
      const fields = {
        description: !!gmbProfile?.profile?.description,
        websiteUri: shallowProfile
          ? !!shallowProfile?.website
          : !!gmbProfile?.websiteUri,
        primaryCategory: shallowProfile
          ? !!shallowProfile?.primary_category
          : !!gmbProfile?.categories?.primaryCategory?.displayName,
        additionalCategories: shallowProfile
          ? !!(shallowProfile?.additional_categories?.length > 0)
          : !!(gmbProfile?.categories?.additionalCategories?.length > 0),
        regularHours: shallowProfile
          ? !!(shallowProfile?.opening_hours?.length > 0)
          : !!(gmbProfile?.regularHours?.periods?.length > 0),
        phoneNumber: shallowProfile
          ? !!shallowProfile?.phone
          : !!gmbProfile?.phoneNumbers?.primaryPhone,
        openDate: shallowProfile
          ? !!shallowProfile?.opening_date
          : !!gmbProfile?.openInfo?.openingDate,
        postCount: shallowProfile
          ? (shallowProfile?.post_count || 0) > 0
          : (gmbProfile?.postCount || 0) > 0,
        hasLogo: shallowProfile
          ? !!shallowProfile?.logo
          : !!gmbProfile?.mediaItems?.logo,
        hasCoverPhoto: shallowProfile
          ? !!shallowProfile?.cover_photo
          : !!gmbProfile?.mediaItems?.coverPhoto,
        hasPhotos: shallowProfile
          ? (shallowProfile?.photos_length || 0) > 0
          : (gmbProfile?.mediaItems?.photosLength || 0) > 0,
        attributeCount: shallowProfile
          ? (shallowProfile?.attribute_count || 0) > 0
          : (gmbProfile?.attributeCount || 0) > 0,
        hasSocialLinks: shallowProfile
          ? shallowProfile?.has_social_links === true
          : gmbProfile?.socialInfo?.found === true,
      };

      const totalFields = Object.keys(fields).length;
      const filledFields = Object.values(fields).filter(Boolean).length;
      return Math.round((filledFields / totalFields) * 100);
    },

    async fetchPlatformLogo(platform) {
      if (this.platformLogoCache[platform]) {
        return;
      }

      try {
        const platformDoc = await getDoc(
          doc(db, "fly_platforms", platform.toLowerCase())
        );
        if (platformDoc.exists()) {
          const logoUrl = platformDoc.data().logo_url;
          this.$set(this.platformLogoCache, platform, logoUrl);
        }
      } catch (error) {
        console.error("Error fetching platform logo:", error);
      }
    },

    fetchAllPlatformLogos() {
      // Always pre-fetch Google Business Profile logo
      this.fetchPlatformLogo("google_business_profile");

      // Fetch logos for all connected apps across all locations
      this.locations.forEach((location) => {
        if (location.connected_apps && Array.isArray(location.connected_apps)) {
          location.connected_apps.forEach((platform) => {
            this.fetchPlatformLogo(platform);
          });
        }
      });
    },

    toggleCountry(country) {
      const index = this.expandedCountries.indexOf(country);
      if (index > -1) {
        this.expandedCountries.splice(index, 1);
      } else {
        this.expandedCountries.push(country);
      }
    },

    toggleState(country, state) {
      const stateKey = `${country}-${state}`;
      const index = this.expandedStates.indexOf(stateKey);
      if (index > -1) {
        this.expandedStates.splice(index, 1);
      } else {
        this.expandedStates.push(stateKey);
      }
    },

    selectLocation(location) {
      this.selectedLocation = location;
      // Expand treeview for selected location
      if (location) {
        const country = location.country || 'Other Countries';
        const state = location.state || 'Other States';
        if (!this.expandedCountries.includes(country)) {
          this.expandedCountries.push(country);
        }
        const stateKey = `${country}-${state}`;
        if (!this.expandedStates.includes(stateKey)) {
          this.expandedStates.push(stateKey);
        }
      }
      
      // Update URL with SEO-friendly store name (only if navigation is not disabled)
      if (location && (location.name || location.storeLocation) && !this.disableNavigation) {
        const storeName = location.name || location.storeLocation;
        // Slugify: replace spaces/special chars with hyphens, lowercase
        const slug = storeName
          .toString()
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        
        if (this.isSubdomain) {
          // For subdomain, use just the slug (no store-locator prefix)
          this.$router.replace({
            name: 'subdomain-page',
            params: { slug }
          });
        } else {
        // Use new URL format: <uid>/<wid>/<slug>
        const uid = this.embedUid || this.$route.params.uid;
        const wid = this.embedWid || this.$route.params.wid;
        if (uid && wid) {
          this.$router.replace({
            name: 'store-locator',
            params: { uid, wid, slug }
          });
          }
        }
      }
      if (this.isMobile) this.closeLocationList();
    },

    getLocationStatus(location) {
      if (location.shallowConnect) return 'Shallow Connected';
      if (location.status === 'connected') return 'Connected';
      if (location.status === 'accepted') return 'Accepted';
      if (location.status === 'invited') return 'Invited';
      return 'Unknown';
    },

    getLocationStatusColor(location) {
      if (location.shallowConnect) return 'bg-blue-500';
      if (location.status === 'connected') return 'bg-green-500';
      if (location.status === 'accepted') return 'bg-yellow-500';
      if (location.status === 'invited') return 'bg-purple-500';
      return 'bg-gray-500';
    },

    getStatusBadgeClass(location) {
      if (location.shallowConnect) return 'bg-blue-50 text-blue-700 border border-blue-200';
      if (location.status === 'connected') return 'bg-green-50 text-green-700 border border-green-200';
      if (location.status === 'accepted') return 'bg-yellow-50 text-yellow-700 border border-yellow-200';
      if (location.status === 'invited') return 'bg-purple-50 text-purple-700 border border-purple-200';
      return 'bg-gray-50 text-gray-700 border border-gray-200';
    },

    getProfileHealthColor(score) {
      if (score >= 80) return 'bg-green-500';
      if (score >= 60) return 'bg-yellow-500';
      if (score >= 40) return 'bg-orange-500';
      return 'bg-red-500';
    },

    getFullAddress(location) {
      // Prefer full address fields if available
      if (location?.address) return location.address;
      if (location?.gmbProfile?.storefrontAddress?.addressLines?.length) {
        return location.gmbProfile.storefrontAddress.addressLines.join(', ');
      }
      if (location?.gmbProfile?.address) return location.gmbProfile.address;
      // Fallback to current logic, but include location name
      const parts = [
        location.name || location.storeLocation,
        location.sub_locality,
        location.city,
        location.state,
        location.zipcode,
        location.country
      ].filter(Boolean);
      return parts.join(', ');
    },

    refreshLocations() {
      this.fetchLocations();
    },

    // Methods for full profile view
    getBusinessDescription(location) {
      return location?.gmbProfile?.profile?.description || 
             location?.gmb_shallow_profile_data?.description || 
             null;
    },
    getProductsServicesSectionName() {
      // Get section name from GMB settings data (highest priority)
      if (this.gmbSettingsData?.productsServicesSectionName) {
        return this.gmbSettingsData.productsServicesSectionName;
      }
      // Default fallback
      return "Products & Services";
    },
    getProductsServicesSectionSubtext() {
      // Get section subtext from GMB settings data (highest priority)
      if (this.gmbSettingsData?.productsServicesSectionSubtext) {
        return this.gmbSettingsData.productsServicesSectionSubtext;
      }
      // Default fallback
      return "Explore our comprehensive range of products and services";
    },

    getCoverPhoto(location) {
      // For shallow profile data
      if (location?.gmb_shallow_profile_data?.cover_photo) {
        return location.gmb_shallow_profile_data.cover_photo;
      }
      
      // For deep profile data
      if (location?.gmbProfile?.mediaItems?.coverPhoto?.googleUrl) {
        return location.gmbProfile.mediaItems.coverPhoto.googleUrl;
      }
      
      return null;
    },

    getSocialLinks(location) {
      const links = [];
      
      console.log('[getSocialLinks] Checking social links for location:', location?.name || location?.storeLocation);
      console.log('[getSocialLinks] Location social_links:', location?.social_links);
      console.log('[getSocialLinks] GMB settings data:', this.gmbSettingsData);
      console.log('[getSocialLinks] Workspace social_links:', this.workspaceData?.social_links);
      
      // First priority: individual store configuration social_links
      if (location?.social_links) {
        console.log('[getSocialLinks] Found individual store social_links');
        Object.entries(location.social_links).forEach(([platform, url]) => {
          if (url && url.trim() !== '') {
            const platformConfig = this.getSocialPlatformConfig(platform);
            if (platformConfig) {
              links.push({
                platform: platformConfig.name,
                url: url,
                icon: platformConfig.icon,
                iconColor: platformConfig.iconColor
              });
            }
          }
        });
      }
      
      // Second priority: location.gmbProfile.socialInfo.links (only if no links found from first source)
      if (links.length === 0 && location?.gmbProfile?.socialInfo?.links && Array.isArray(location.gmbProfile.socialInfo.links)) {
        location.gmbProfile.socialInfo.links.forEach(link => {
          if (link.url && link.platform) {
            const platformConfig = this.getSocialPlatformConfig(link.platform);
            if (platformConfig) {
              links.push({
                platform: platformConfig.name,
                url: link.url,
                icon: platformConfig.icon,
                iconColor: platformConfig.iconColor
              });
            }
          }
        });
      }
      
      // Third priority: GMB settings social_links (only if no links found from first two sources)
      if (links.length === 0 && this.gmbSettingsData?.social_links) {
        console.log('[getSocialLinks] Found GMB settings social_links');
        Object.entries(this.gmbSettingsData.social_links).forEach(([platform, url]) => {
          if (url && url.trim() !== '') {
            const platformConfig = this.getSocialPlatformConfig(platform);
            if (platformConfig) {
              links.push({
                platform: platformConfig.name,
                url: url,
                icon: platformConfig.icon,
                iconColor: platformConfig.iconColor
              });
            }
          }
        });
      }
      
      // Fourth priority: workspace social_links (only if no links found from first three sources)
      if (links.length === 0 && this.workspaceData?.social_links) {
        console.log('[getSocialLinks] Found workspace social_links');
        Object.entries(this.workspaceData.social_links).forEach(([platform, url]) => {
          if (url && url.trim() !== '') {
            const platformConfig = this.getSocialPlatformConfig(platform);
            if (platformConfig) {
              links.push({
                platform: platformConfig.name,
                url: url,
                icon: platformConfig.icon,
                iconColor: platformConfig.iconColor
              });
            }
          }
        });
      }
      
      // Fifth priority: Check for social links in attributes (deep profile) - legacy support
      if (links.length === 0 && location?.gmb_attributes && Array.isArray(location.gmb_attributes)) {
        const socialPlatforms = {
          'attributes/url_facebook': { platform: 'Facebook', icon: 'mdi-facebook', iconColor: '#1877F2' },
          'attributes/url_instagram': { platform: 'Instagram', icon: 'mdi-instagram', iconColor: '#E4405F' },
          'attributes/url_twitter': { platform: 'Twitter', icon: 'mdi-twitter', iconColor: '#1DA1F2' },
          'attributes/url_linkedin': { platform: 'LinkedIn', icon: 'mdi-linkedin', iconColor: '#0A66C2' },
          'attributes/url_pinterest': { platform: 'Pinterest', icon: 'mdi-pinterest', iconColor: '#BD081C' },
          'attributes/url_youtube': { platform: 'YouTube', icon: 'mdi-youtube', iconColor: '#FF0000' }
        };
        
        location.gmb_attributes.forEach(attr => {
          if (socialPlatforms[attr.name] && attr.value) {
            links.push({
              platform: socialPlatforms[attr.name].platform,
              url: attr.value,
              icon: socialPlatforms[attr.name].icon,
              iconColor: socialPlatforms[attr.name].iconColor
            });
          }
        });
      }
      
      // Sixth priority: Check for social links in shallow profile data - legacy support
      if (links.length === 0 && location?.gmb_shallow_profile_data?.social_links && Array.isArray(location.gmb_shallow_profile_data.social_links)) {
        location.gmb_shallow_profile_data.social_links.forEach(link => {
          if (link.url && link.platform) {
            const platformConfig = this.getSocialPlatformConfig(link.platform);
            if (platformConfig) {
              links.push({
                platform: link.platform.charAt(0).toUpperCase() + link.platform.slice(1),
                url: link.url,
                icon: platformConfig.icon,
                iconColor: platformConfig.iconColor
              });
            }
          }
        });
      }
      
      console.log('[getSocialLinks] Final social links found:', links);
      return links;
    },

    getSocialPlatformConfig(platform) {
      const platformConfigs = {
        facebook: { 
          name: 'Facebook', 
          icon: 'mdi-facebook', 
          iconColor: '#1877F2' 
        },
        instagram: { 
          name: 'Instagram', 
          icon: 'mdi-instagram', 
          iconColor: '#E4405F' 
        },
        twitter: { 
          name: 'Twitter', 
          icon: 'mdi-twitter', 
          iconColor: '#1DA1F2' 
        },
        youtube: { 
          name: 'YouTube', 
          icon: 'mdi-youtube', 
          iconColor: '#FF0000' 
        },
        threads: { 
          name: 'Threads', 
          icon: 'mdi-message-text', 
          iconColor: '#000000' 
        },
        linkedin: { 
          name: 'LinkedIn', 
          icon: 'mdi-linkedin', 
          iconColor: '#0A66C2' 
        },
        tiktok: { 
          name: 'TikTok', 
          icon: 'mdi-music-note', 
          iconColor: '#000000' 
        },
        whatsapp: { 
          name: 'WhatsApp', 
          icon: 'mdi-whatsapp', 
          iconColor: '#25D366' 
        }
      };
      
      // Handle both lowercase and original case
      const normalizedPlatform = platform.toLowerCase();
      return platformConfigs[normalizedPlatform] || null;
    },

    handleCoverPhotoError(event) {
      console.error('Cover photo failed to load:', event.target.src);
      // Set error flag to hide the cover photo panel
      this.coverPhotoError = true;
      this.coverPhotoLoading = false;
    },

    handleCoverPhotoLoad() {
      console.log("Cover photo loaded successfully");
      this.coverPhotoError = false;
      this.coverPhotoLoading = false;
    },

    getOpeningHours(location) {
      // For shallow profile data
      if (location?.gmb_shallow_profile_data?.opening_hours) {
        return location.gmb_shallow_profile_data.opening_hours.map(hours => ({
          day: this.formatDayName(hours.day),
          hours: hours.hours
        }));
      }
      
      // For deep profile data
      if (location?.gmbProfile?.regularHours?.periods) {
        const daysMap = {
          'MONDAY': 'Monday',
          'TUESDAY': 'Tuesday', 
          'WEDNESDAY': 'Wednesday',
          'THURSDAY': 'Thursday',
          'FRIDAY': 'Friday',
          'SATURDAY': 'Saturday',
          'SUNDAY': 'Sunday'
        };
        
        return location.gmbProfile.regularHours.periods.map(period => ({
          day: daysMap[period.openDay] || period.openDay,
          hours: period.openTime && period.closeTime ? 
                 `${period.openTime.hours}:${period.openTime.minutes?.toString().padStart(2, '0') || '00'} - ${period.closeTime.hours}:${period.closeTime.minutes?.toString().padStart(2, '0') || '00'}` : 
                 'Closed'
        }));
      }
      
      return [];
    },

    getPrimaryCategory(location) {
      return location?.gmb_shallow_profile_data?.primary_category ||
             location?.gmbProfile?.categories?.primaryCategory?.displayName ||
             'Business';
    },

    getAdditionalCategories(location) {
      if (location?.gmb_shallow_profile_data?.additional_categories) {
        return location.gmb_shallow_profile_data.additional_categories;
      }
      
      if (location?.gmbProfile?.categories?.additionalCategories) {
        return location.gmbProfile.categories.additionalCategories.map(cat => cat.displayName);
      }
      
      return [];
    },

    formatDayName(day) {
      const daysMap = {
        'monday': 'Monday',
        'tuesday': 'Tuesday',
        'wednesday': 'Wednesday', 
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday',
        'sunday': 'Sunday'
      };
      return daysMap[day?.toLowerCase()] || day;
    },

    formatWebsite(website) {
      if (!website) return '';
      return website.replace(/^https?:\/\//, '').replace(/^www\./, '');
    },

    isCurrentDay(day) {
      const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
      return day?.toLowerCase() === today.toLowerCase();
    },

    callBusiness() {
      if (this.selectedLocation?.phone) {
        window.location.href = `tel:${this.selectedLocation.phone}`;
      }
    },

    visitWebsite() {
      if (this.selectedLocation?.website) {
        window.open(this.selectedLocation.website, '_blank');
      }
    },

    getWebsiteUrl() {
      // First try to get from selected location
      if (this.selectedLocation?.website) {
        return this.selectedLocation.website;
      }
      
      // Then try to get from first location
      if (this.locations.length > 0 && this.locations[0]?.website) {
        return this.locations[0].website;
      }
      
      // Finally try to get from workspace data
      if (this.workspaceData?.website) {
        return this.workspaceData.website;
      }
      
      return null;
    },

    async fetchApifyLatLng(locationId) {
      try {
        console.log('[fetchApifyLatLng] Fetching Apify lat/lng for locationId:', locationId);
        // Only use the subcollection with random doc IDs
          const apifyColRef = collection(db, "users", this.currentUID, "fly_workspaces", locationId, "gmb_locations_apify_profiles");
          const apifyColSnap = await getDocs(apifyColRef);
        console.log('[fetchApifyLatLng] Documents found:', apifyColSnap.docs.length);
        let found = false;
          for (const docSnap of apifyColSnap.docs) {
            const data = docSnap.data();
          console.log('[fetchApifyLatLng] Checking doc:', docSnap.id, data);
          if (data && data.location && typeof data.location.lat === 'number' && typeof data.location.lng === 'number') {
            this.apifyLat = data.location.lat;
            this.apifyLng = data.location.lng;
            console.log('[fetchApifyLatLng] Found lat/lng:', this.apifyLat, this.apifyLng);
            found = true;
              break;
            }
          }
        if (!found) {
          this.apifyLat = null;
          this.apifyLng = null;
          console.log('[fetchApifyLatLng] No lat/lng found in any Apify profile doc.');
        }
      } catch (e) {
        this.apifyLat = null;
        this.apifyLng = null;
        console.error('[fetchApifyLatLng] Error:', e);
      }
    },

    handleLogoError() {
      console.error("❌ [BRAND LOGO] Logo image failed to load");
      console.error("❌ [BRAND LOGO] Failed logo URL:", this.storeLogo);
      this.logoLoadError = true;
    },

    handleLogoLoad() {
      console.log("✅ [BRAND LOGO] Logo image loaded successfully");
      console.log("✅ [BRAND LOGO] Successfully loaded logo URL:", this.storeLogo);
      this.logoLoaded = true;
    },

    updateFavicon() {
      // Don't update favicon if disabled
      if (this.disableFavicon) {
        console.log("🚫 [BRAND LOGO] Favicon updates disabled");
        return;
      }
      
      // Only update favicon if we have a valid logo URL (not the default SVG)
      const logoUrl = this.storeLogo;
      const defaultSvg = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+';
      
      if (!logoUrl || logoUrl === defaultSvg) {
        console.log("🚫 [BRAND LOGO] No valid logo URL available for favicon");
        console.log("🚫 [BRAND LOGO] Logo URL:", logoUrl);
        console.log("🚫 [BRAND LOGO] Is default SVG:", logoUrl === defaultSvg);
        return;
      }

      console.log("🔄 [BRAND LOGO] Updating favicon with logo:", logoUrl);

      // Remove existing favicon links
      const existingFavicons = document.querySelectorAll("link[rel~='icon']");
      existingFavicons.forEach(link => {
        if (link.href !== this._originalFaviconHref) {
          link.remove();
        }
      });

      // Create new favicon link
      const link = document.createElement('link');
      link.rel = 'icon';
      link.href = logoUrl;
      link.type = 'image/x-icon';
      document.head.appendChild(link);

      console.log("✅ [BRAND LOGO] Favicon updated successfully with URL:", logoUrl);
    },

    updatePageTitle() {
      // Don't update page title if disabled
      if (this.disablePageTitle) {
        console.log("Page title updates disabled");
        return;
      }
      
      if (this.brandName && this.brandName !== 'Loading...') {
        let newTitle = `${this.brandName} - Store Locator`;
        
        // Add location count if available
        if (this.totalLocations > 0) {
          newTitle += ` (${this.totalLocations} locations)`;
        }
        
        console.log("Updating page title to:", newTitle);
        document.title = newTitle;
      }
    },

    selectHeaderTab(tabValue) {
      // If the tab value looks like a URL, open it in a new tab
      if (typeof tabValue === 'string' && /^https?:\/\//.test(tabValue)) {
        window.open(tabValue, '_blank');
        return;
      }
      this.selectedTab = tabValue;
      this.$emit('tab-change', tabValue);
    },

    getDirections() {
      // Prefer coordinates if available
      if (this.apifyLat && this.apifyLng) {
        const url = `https://www.google.com/maps/dir/?api=1&destination=${this.apifyLat},${this.apifyLng}`;
        window.open(url, '_blank');
      } else if (this.selectedLocation) {
        const address = encodeURIComponent(this.getFullAddress(this.selectedLocation));
        const url = `https://www.google.com/maps/dir/?api=1&destination=${address}`;
        window.open(url, '_blank');
      }
    },

    getGoogleMapsSrc() {
      console.log('[getGoogleMapsSrc] Called. apifyLat:', this.apifyLat, 'apifyLng:', this.apifyLng);
      if (this.apifyLat && this.apifyLng) {
        const url = `https://www.google.com/maps?q=${this.apifyLat},${this.apifyLng}&output=embed`;
        console.log('[getGoogleMapsSrc] Using lat/lng URL:', url);
        return url;
      }
      if (this.selectedLocation) {
        // Include shop name in the address string for Google Maps
        const addrParts = [
          this.selectedLocation.name || this.selectedLocation.storeLocation,
          this.getFullAddress(this.selectedLocation)
        ].filter(Boolean);
        const addr = addrParts.join(', ');
        const url = `https://www.google.com/maps?q=${encodeURIComponent(addr)}&output=embed`;
        console.log('[getGoogleMapsSrc] Using address string URL:', url);
        return url;
      }
      console.log('[getGoogleMapsSrc] No lat/lng or address available, returning empty string.');
      return '';
    },

    async fetchReviewsForLocation(location) {
      this.isReviewsLoading = true;
      this.reviews = [];
      // Reset pagination when fetching new reviews
      this.reviewsPage = 1;
      try {
        // Prepare account info for the selected location
        const currentUID = this.currentUID;
        const wid = location.id;
        // Get workspace details
        const workspaceRef = doc(db, "users", currentUID, "fly_workspaces", wid);
        const workspaceDoc = await getDoc(workspaceRef);
        let isShallow = false;
        let placeId = workspaceDoc.data()?.placeId;
        if (workspaceDoc.exists() && workspaceDoc.data()?.shallowConnect) {
          isShallow = true;
        }
        let accounts = [];
        if (isShallow) {
          accounts.push({
            uid: currentUID,
            wid,
            location: placeId,
            isShallow: true
          });
        } else {
          // For regular workspaces, check for GMB connection
          const connectedAppsRef = collection(db, "users", currentUID, "fly_workspaces", wid, "connected-apps");
          const gbpSnapshot = await getDocs(query(connectedAppsRef, where("__name__", "==", "google_business_profile")));
          if (!gbpSnapshot.empty) {
            const gbpData = gbpSnapshot.docs[0].data();
            const accountId = gbpData.accounts?.[0]?.id || "";
            (gbpData.accounts || []).forEach((account) => {
              const locationId = account.locations?.[0]?.name || "";
              accounts.push({
                uid: currentUID,
                wid,
                location: locationId,
                account: accountId,
                isShallow: false
              });
            });
          }
        }
        if (accounts.length === 0) {
          this.reviews = [];
          return;
        }
        const payload = {
          uid: currentUID,
          accounts,
          action: "get_gmb_reviews",
          platform: "google_business_profile",
          page: 1,
          pageSize: 10, // Limit to 10 reviews
        };
        console.log('[fetchReviewsForLocation] Payload:', payload);
        let idToken = null;
        if (auth.currentUser) {
          idToken = await auth.currentUser.getIdToken();
        }
        const headers = {
          "Content-Type": "application/json",
        };
        if (idToken) {
          headers["Authorization"] = `Bearer ${idToken}`;
        }
        console.log('[fetchReviewsForLocation] ID Token:', idToken);
        console.log('[fetchReviewsForLocation] Endpoint:', `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/GmbReviewsManagement`);
        const response = await fetch(
          `${process.env.VUE_APP_ENGINE_ENDPOINT}/functions/GmbReviewsManagement`,
          {
            method: "POST",
            headers,
            body: JSON.stringify({ data: payload }),
          }
        );
        console.log('[fetchReviewsForLocation] Response:', response);
        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            const reviews = result?.data?.reviews || [];
            const ratingMap = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
            this.reviews = reviews
              .map((review) => {
                return {
                  reviewId: review.reviewId,
                  displayName: review.reviewer?.displayName || "Anonymous",
                  profilePhotoUrl: review.reviewer?.profilePhotoUrl || 'https://www.gravatar.com/avatar/?d=mp',
                  starRating: ratingMap[review.starRating] || 0,
                  comment: review.comment || "",
                  updateTime: review.updateTime,
                };
              })
              .filter((review) => {
                // Only include reviews with 3 stars and above
                const hasGoodRating = review.starRating >= 3;
                
                return hasGoodRating;
              })
              .sort((a, b) => new Date(b.updateTime) - new Date(a.updateTime)) // Sort by latest first
              .slice(0, 10); // Ensure we only keep the latest 10 reviews
          }
        }
        console.log('[fetchReviewsForLocation] Reviews:', this.reviews);
      } catch (e) {
        this.reviews = [];
      } finally {
        this.isReviewsLoading = false;
      }
    },
    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    },

    async fetchPostsForLocation(location) {
      this.isPostsLoading = true;
      this.posts = [];
      // Reset expanded posts state when fetching new posts
      this.expandedPosts = {};
      try {
        if (!location || !location.id) {
          console.log('[fetchPostsForLocation] No location or location ID provided');
          return;
        }

        console.log('[fetchPostsForLocation] Fetching posts for location:', location.id);
        console.log('[fetchPostsForLocation] Using location ID instead of workspace ID');
        
        // Use direct Firestore query for the specific location
        const vm = this;
        let currentUID = this.currentUID || vm.uid;
        if (!vm.currentUID || !location.id) return;
        
        // Use location.id instead of vm.WID to fetch posts for the specific store
        let col = collection(
          colUsers,
          currentUID,
          "fly_workspaces",
          location.id, // Use location.id instead of vm.WID
          "social-posts"
        );
        
        // Build query for published posts (latest first)
        let queries = [
          orderBy("created_at", "desc"),
          where("post_status", "==", "published"),
          limit(10)
        ];
        
        let q = query(col, ...queries);
        let docs = (await getDocs(q)).docs;

        let allPosts = docs.map((i) => {
          return {
            ...i.data(),
            id: i.id,
          };
        });

        const allPlatforms = await vm.getAllPlatforms();

        allPosts.map((post) => {
          let socialMediaArr = [];
          const socialMedias = post.social_media;

          function filteredApp(name) {
            const item = allPlatforms.filter((data) => {
              return data.alias == name;
            });

            return item;
          }

          socialMedias.map((media) => {
            const filteredItem = filteredApp(media);

            let data = {
              name: media,
              label: filteredItem[0]?.name,
              logo_url: filteredItem[0]?.logo_url,
            };
            socialMediaArr.push(data);
          });

          post.social_media = socialMediaArr;
        });

        this.posts = allPosts;
        console.log('[fetchPostsForLocation] Fetched posts for location', location.id, ':', this.posts.length);
        
      } catch (error) {
        console.error('[fetchPostsForLocation] Error fetching posts for location', location.id, ':', error);
        // Fallback: try to get posts from location data if available
        if (location.recent_posts && Array.isArray(location.recent_posts)) {
          console.log('[fetchPostsForLocation] Using fallback posts from location data');
          this.posts = location.recent_posts;
        } else {
          this.posts = [];
        }
      } finally {
        this.isPostsLoading = false;
      }
    },

    // Add pagination methods for reviews
    nextReviewPage() {
      if (this.hasNextReviewPage) {
        this.reviewsPage++;
      }
    },

    prevReviewPage() {
      if (this.hasPrevReviewPage) {
        this.reviewsPage--;
      }
    },

    toggleReadMore(postId) {
      console.log('[toggleReadMore] Toggling post ID:', postId);
      console.log('[toggleReadMore] Current expandedPosts state:', this.expandedPosts);
      console.log('[toggleReadMore] Current state for this post:', this.expandedPosts[postId]);
      
      // Use Vue.set to ensure reactivity
      this.$set(this.expandedPosts, postId, !this.expandedPosts[postId]);
      
      console.log('[toggleReadMore] New state for this post:', this.expandedPosts[postId]);
      console.log('[toggleReadMore] Updated expandedPosts state:', this.expandedPosts);
    },

    // SEO Methods
    updateSEO() {
      console.log('[StoreLocator] Updating SEO meta tags');
      
      if (!this.workspaceData) {
        console.log('[StoreLocator] No workspace data available for SEO');
        return;
      }

      // Generate SEO content
      const title = generateStoreLocatorTitle(this.workspaceData, this.totalLocations);
      const description = generateStoreLocatorDescription(this.workspaceData, this.totalLocations);
      const keywords = this.generateStoreLocatorKeywords();
      const image = this.getSEOImage();
      const url = this.getCurrentURL();

      console.log('[StoreLocator] SEO content generated:', { title, description, keywords, image, url });

      // Update meta tags
      updateMetaTags({
        title,
        description,
        keywords,
        image,
        url,
        type: 'website',
        siteName: this.brandName
      });

      // Generate and inject structured data
      const structuredData = generateStoreLocatorStructuredData(this.workspaceData, this.locations);
      injectStructuredData(structuredData);

      console.log('[StoreLocator] SEO meta tags and structured data updated');
    },

    generateStoreLocatorKeywords() {
      const keywords = [];
      
      // Add brand name
      if (this.brandName && this.brandName !== 'Loading...') {
        keywords.push(this.brandName);
      }
      
      // Add location-related keywords
      keywords.push('store locator', 'find locations', 'business locations', 'store finder');
      
      // Add location cities if available
      const cities = this.uniqueCities.slice(0, 5); // Limit to first 5 cities
      keywords.push(...cities);
      
      // Add business category if available
      if (this.locations.length > 0) {
        const firstLocation = this.locations[0];
        const category = firstLocation?.gmbProfile?.categories?.primaryCategory?.displayName ||
                         firstLocation?.gmb_shallow_profile_data?.primary_category;
        if (category) {
          keywords.push(category);
        }
      }
      
      return keywords.join(', ');
    },

    getSEOImage() {
      // Use workspace logo as OG image
      const logo = this.storeLogo;
      if (logo && logo !== 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjQiIGhlaWdodD0iNjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cGF0aCBkPSJNMTIgMkM2LjQ4IDIgMiA2LjQ4IDIgMTJzNC40OCAxMCAxMCAxMCAxMC00LjQ4IDEwLTEwUzE3LjUyIDIgMTIgMnptMCAxOGMtNC40MSAwLTgtMy41OS04LThzMy41OS04IDgtOCA4IDMuNTkgOCA4LTMuNTkgOC04IDh6IiBmaWxsPSIjZTVlN2ViIi8+PC9zdmc+') {
        return logo;
      }
      
      // Fallback to cover photo from first location
      if (this.locations.length > 0) {
        const firstLocation = this.locations[0];
        return this.getCoverPhoto(firstLocation);
      }
      
      return null;
    },

    getCurrentURL() {
      // Get current URL for canonical tag
      if (typeof window !== 'undefined') {
        return window.location.href;
      }
      return '';
    },
    openLocationList() { this.showLocationList = true; },
    closeLocationList() { this.showLocationList = false; },
    
    // Mobile side panel methods
    toggleMobileSidePanel() {
      this.showMobileSidePanel = !this.showMobileSidePanel;
    },
    
    closeMobileSidePanel() {
      this.showMobileSidePanel = false;
    },
    
    selectLocationAndClose(location) {
      this.selectLocation(location);
      this.closeMobileSidePanel();
    },
    

    
    async fetchWidgetData() {
      if (!this.isWidget) {
        console.log("🔧 [Widget] Not running as widget, skipping widget data fetch");
        return;
      }
      
      // Extract widget key from URL like Dashboard.vue and Calendar.vue
      const widgetKey = this.$route.params.id;
      if (!widgetKey) {
        console.log("🔧 [Widget] No widget key in route params");
        return;
      }
      
      try {
        this.isWidgetLoading = true;
        console.log("🔧 [Widget] Fetching widget data for key:", widgetKey);
        console.log("🔧 [Widget] Current URL:", window.location.href);
        console.log("🔧 [Widget] Route params:", this.$route.params);
        
        // Import Firebase functions
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/utils/firebase.utils');
        
        console.log("🔧 [Widget] Firebase db instance:", db);
        console.log("🔧 [Widget] Firebase project ID:", db.app.options.projectId);
        
        const widgetRef = doc(db, 'widget_keys', widgetKey);
        console.log("🔧 [Widget] Widget document reference:", widgetRef);
        console.log("🔧 [Widget] Widget document path:", widgetRef.path);
        
        const widgetDoc = await getDoc(widgetRef);
        console.log("🔧 [Widget] Widget document exists:", widgetDoc.exists());
        
        if (widgetDoc.exists()) {
          this.widgetData = widgetDoc.data();
          this.widgetKey = widgetKey;
          console.log("🔧 [Widget] Widget data fetched successfully:", this.widgetData);
          console.log("🔧 [Widget] Widget UID:", this.widgetData.uid);
          console.log("🔧 [Widget] Widget WID:", this.widgetData.wid);
        } else {
          console.log("🔧 [Widget] Widget key not found in database");
          console.log("🔧 [Widget] Widget key searched:", widgetKey);
          console.log("🔧 [Widget] Collection path: widget_keys");
        }
      } catch (error) {
        console.error("🔧 [Widget] Error fetching widget data:", error);
        console.error("🔧 [Widget] Error details:", {
          name: error.name,
          message: error.message,
          code: error.code,
          stack: error.stack
        });
      } finally {
        this.isWidgetLoading = false;
        console.log("🔧 [Widget] Widget loading state set to false");
      }
    },

    // Post modal methods
    openPostModal(post) {
      this.selectedPost = post;
      this.showPostModal = true;
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    },

    closePostModal() {
      this.showPostModal = false;
      this.selectedPost = null;
      // Restore body scroll
      document.body.style.overflow = 'auto';
    },
    
    // Initialize widget
  },

  beforeDestroy() {
    // Clean up resize listener
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
  },

  async mounted() {
    console.log("=== STORELOCATOR MOUNTED START ===");
    console.log("🔧 StoreLocator component mounted with props:", {
      embedUid: this.embedUid,
      embedWid: this.embedWid,
      embedSlug: this.embedSlug,
      WID: this.WID,
      currentUID: this.currentUID,
      isSubdomain: this.isSubdomain,
      subdomainName: this.subdomainName,
      isWidget: this.isWidget,
      isStoreLocatorWidget: this.isStoreLocatorWidget
    });
    console.log("🔧 Current URL:", window.location.href);
    console.log("🔧 Current hostname:", window.location.hostname);
    console.log("🔧 Current pathname:", window.location.pathname);
    console.log("🔧 Route name:", this.$route.name);
    console.log("🔧 Route params:", this.$route.params);

    // Add window resize listener for responsive behavior
    this.handleResize = () => {
      this.isMobile = window.innerWidth <= 1024;
      this.isSmallScreen = window.innerWidth <= 768;
    };
    window.addEventListener('resize', this.handleResize);

    // Check if PostCard component is available
    console.log('[StoreLocator] PostCard component check:', {
      PostCard: typeof PostCard,
      PostCardName: PostCard?.name,
      PostCardOptions: PostCard?.options
    });

    // For subdomain access, fetch subdomain data first
    if (this.isSubdomain) {
      console.log('🔄 [StoreLocator] On subdomain, fetching subdomain data first');
      console.log('🔄 [StoreLocator] Subdomain name:', this.subdomainName);
      console.log('🔄 [StoreLocator] About to call fetchSubdomainData()');
      
      await this.fetchSubdomainData();
      
      console.log('🔄 [StoreLocator] fetchSubdomainData() completed');
      console.log('🔄 [StoreLocator] subdomainData after fetch:', this.subdomainData);
      
      // If subdomain data fetch failed, don't proceed
      if (!this.subdomainData) {
        console.error('❌ [StoreLocator] Failed to fetch subdomain data, stopping initialization');
        console.error('❌ [StoreLocator] This could be due to:');
        console.error('❌ [StoreLocator] 1. Network connectivity issues');
        console.error('❌ [StoreLocator] 2. Firebase configuration problems');
        console.error('❌ [StoreLocator] 3. Document not existing in unique_domain collection');
        console.error('❌ [StoreLocator] 4. Firebase security rules blocking access');
        return;
      }
      
      console.log('✅ [StoreLocator] Subdomain data fetched successfully, continuing initialization');
    } else {
      console.log('ℹ️ [StoreLocator] Not on subdomain, proceeding with regular initialization');
    }

    // Handle widget-specific initialization
    if (this.isWidget) {
      console.log('🔧 [StoreLocator] Running as widget, initializing widget mode');
      await this.initializeWidget();
      return;
    }

    // For regular embed access, check authentication (skip for widgets)
    if (!this.isSubdomain && !this.isWidget && (
      (auth.currentUser && auth.currentUser.uid !== this.currentUID) ||
      this.currentWorkspace?.storeWorkspace
    )) {
      this.$snackbar.show("Direct access is prohibited");
      this.$router.push("/calendar");
      return;
    }

    // Store original favicon reference
    const favicon = document.querySelector("link[rel~='icon']");
    if (favicon) {
      this._originalFaviconHref = favicon.href;
    }

    // Store original page title
    this._originalPageTitle = document.title;

    // Add a safety timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (this.isLoading) {
        console.warn('[StoreLocator] Loading timeout reached, forcing isLoading to false');
        this.isLoading = false;
      }
    }, 10000); // 10 seconds timeout

    // Fetch workspace data and locations
    this.fetchLocations().then(() => {
      clearTimeout(loadingTimeout);
      console.log('[StoreLocator] fetchLocations completed successfully');
      console.log('[StoreLocator] Locations fetched, checking for auto-selection...');
      
      // Handle slug-based selection for new URL format
      if (this.embedSlug && !this.selectedLocation && this.locations && this.locations.length > 0) {
        this.selectLocationBySlug(this.embedSlug);
      }
      
      // Auto-select location based on slug in URL (legacy support)
      const slug = this.$route.params.slug;
      if (slug && !this.selectedLocation && this.locations && this.locations.length > 0) {
        const slugify = str => str && str.toString().trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
        const match = this.locations.find(loc => slugify(loc.name || loc.storeLocation) === slug);
        if (match) {
          console.log('[StoreLocator] Auto-selecting location based on slug:', match);
          this.selectedLocation = match;
          // Expand treeview for selected location
          const country = match.country || 'Other Countries';
          const state = match.state || 'Other States';
          if (!this.expandedCountries.includes(country)) {
            this.expandedCountries.push(country);
          }
          const stateKey = `${country}-${state}`;
          if (!this.expandedStates.includes(stateKey)) {
            this.expandedStates.push(stateKey);
          }
        }
      }
      
      // Update SEO after data is loaded
      if (this.workspaceData && this.locations.length > 0) {
        console.log('[StoreLocator] Initial SEO update');
        this.updateSEO();
      }
    }).catch((error) => {
      clearTimeout(loadingTimeout);
      console.error('[StoreLocator] fetchLocations failed:', error);
      this.isLoading = false;
    });

    // Set default selected tab
    if (this.headerTabs.length && !this.selectedTab) {
      this.selectedTab = this.headerTabs[0].value;
    }

    // Add a window resize listener to update isMobile
    this.handleResize = () => {
      this.isMobile = window.innerWidth <= 768;
    };
    window.addEventListener('resize', this.handleResize);
  },
  
  beforeDestroy() {
    // Restore original favicon
    if (this._originalFaviconHref) {
      const favicon = document.querySelector("link[rel~='icon']");
      if (favicon) {
        favicon.href = this._originalFaviconHref;
        console.log("Restored original favicon:", this._originalFaviconHref);
      }
    }
    
    // Clean up any custom favicon links we created
    const customFavicons = document.querySelectorAll("link[rel~='icon']");
    customFavicons.forEach(link => {
      if (link.href !== this._originalFaviconHref) {
        link.remove();
        console.log("Removed custom favicon link");
      }
    });

    // Restore original page title
    if (this._originalPageTitle) {
      console.log("Restoring original page title:", this._originalPageTitle);
      document.title = this._originalPageTitle;
    }
    
    // Clean up SEO meta tags
    console.log('[StoreLocator] Cleaning up SEO meta tags');
    cleanupMetaTags();

    // Remove the window resize listener
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
  },
};
</script>

<style scoped>
:root {
  --store-primary: #1773EA;
  --store-secondary: #6B7280;
  --store-panel-bg: linear-gradient(90deg, #eff6ff 0%, #e0e7ff 100%);
  --store-header-text: #222;
  --store-body-text: #333;
  --store-border-radius: 1.25rem;
  --store-font-family: Inter, sans-serif;
  --store-button-bg: #1773EA;
  --store-button-text: #fff;
  --store-selected-bg: rgba(23, 115, 234, 0.1);
  --store-selected-border: #1773EA;
  --store-selected-text: #1773EA;
  --store-logo-size-mobile: 2.25rem;
  --store-logo-size-desktop: 3rem;
  --store-header-bg: #fff;
  --store-tab-text: #333;
  --store-tab-hover-text: #1773EA;
  --store-tab-selected-text: #fff;
  --store-tab-bg: #f3f4f6;
  --store-tab-hover-bg: #e0e7ff;
  --store-tab-selected-bg: #1773EA;
  --store-search-border: #bdb4e7;
  --store-search-radius: 0.75rem;
  --store-search-border-size: 2px;
  --store-brand-name-size: 1.375rem;
  --store-brand-name-color: #000000;
  --sidepanel-selected-bg: rgba(23, 115, 234, 0.15);
  --sidepanel-selected-text: rgba(23, 115, 234, 1);
  --sidepanel-selected-border: rgba(23, 115, 234, 1);
  --sidepanel-selected-radius: 1.25rem;
  --sidepanel-hover-bg: rgba(23, 115, 234, 0.08);
  --sidepanel-hover-text: rgba(23, 115, 234, 1);
  --sidepanel-hover-border: rgba(23, 115, 234, 1);
  --sidepanel-hover-border-size: 2px;
  --sidepanel-hover-radius: 1.25rem;
  --sidepanel-unselected-bg: #fff;
  --sidepanel-unselected-text: #222;
  --sidepanel-unselected-border: transparent;
  --sidepanel-unselected-border-size: 1px;
  --sidepanel-unselected-radius: 0.75rem;
  --sidepanel-icon: rgba(23, 115, 234, 1);
  --sidepanel-chevron: rgba(23, 115, 234, 0.7);
  --sidepanel-selected-border-size: 2px;
  --details-address-bg: #f0f0f0;
  --details-address-text: #222;
  --details-address-border: #1773EA;
  --details-address-border-size: 2px;
  --details-address-radius: 1.25rem;
  --details-contact-bg: #f0f0f0;
  --details-contact-text: #222;
  --details-contact-border: #1773EA;
  --details-contact-border-size: 2px;
  --details-contact-radius: 1.25rem;
  --details-description-bg: #f0f0f0;
  --details-description-text: #222;
  --details-description-border: #1773EA;
  --details-description-border-size: 2px;
  --details-description-radius: 1.25rem;
  --details-cover-photo-bg: #f0f0f0;
  --details-cover-photo-text: #222;
  --details-cover-photo-border: #1773EA;
  --details-cover-photo-border-size: 2px;
  --details-cover-photo-radius: 1.25rem;
  --details-social-links-bg: #f0f0f0;
  --details-social-links-text: #222;
  --details-social-links-border: #1773EA;
  --details-social-links-border-size: 2px;
  --details-social-links-radius: 1.25rem;
  --details-hours-bg: #f0f0f0;
  --details-hours-text: #222;
  --details-hours-border: #1773EA;
  --details-hours-border-size: 2px;
  --details-hours-radius: 1.25rem;
  --details-categories-bg: #f0f0f0;
  --details-categories-text: #222;
  --details-categories-border: #1773EA;
  --details-categories-border-size: 2px;
  --details-categories-radius: 1.25rem;
  --details-posts-bg: #f0f0f0;
  --details-posts-text: #222;
  --details-posts-border: #1773EA;
  --details-posts-border-size: 2px;
  --details-posts-radius: 1.25rem;
  --details-actions-bg: #f0f0f0;
  --details-actions-text: #222;
  --details-actions-border: #1773EA;
  --details-actions-border-size: 2px;
  --details-actions-radius: 1.25rem;
  --details-btn-text: #fff;
  --details-btn-bg: #1773EA;
  --details-btn-border: #1773EA;
  --details-btn-radius: 0.75rem;
  --details-btn-border-size: 1.5px;
  --details-btn-hover-text: #fff;
  --details-btn-hover-bg: #125bb5;
  --details-btn-hover-border: #125bb5;
  --details-btn-hover-border-size: 1.5px;
  --details-btn-hover-border-radius: 0.75rem;
  --details-btn-selected-text: #fff;
  --details-btn-selected-bg: #125bb5;
  --details-btn-selected-border: #125bb5;
  --details-btn-selected-border-size: 1.5px;
  --details-btn-selected-border-radius: 0.75rem;
  --details-header-bg: #f0f0f0;
  --details-header-text: #222;
  --details-header-border: #1773EA;
  --details-header-border-size: 2px;
  --details-header-radius: 1.25rem;
  --details-primary: #1976d2;
  --details-secondary: #424242;
  --details-accent: #82B1FF;
  --details-info: #2196F3;
  --details-success: #4CAF50;
  --details-warning: #FFC107;
  --details-error: #FF5252;
  --details-posts-border-size: 2px;
  --details-posts-radius: 1.25rem;
  --details-actions-bg: #f0f0f0;
  --details-actions-text: #222;
  --details-actions-border: #1773EA;
  --details-actions-border-size: 2px;
  --details-actions-radius: 1.25rem;
  --details-reviews-bg: #f0f0f0;
  --details-reviews-text: #222;
  --details-reviews-border: #1773EA;
  --details-reviews-border-size: 2px;
  --details-reviews-radius: 1.25rem;
  --details-btn-text: #fff;
}
.bg-gradient-to-r {
  background: var(--store-panel-bg) !important;
}
.rounded-2xl {
  border-radius: var(--store-border-radius) !important;
}
.text-gray-800, .text-gray-900, h2, h3, h1 {
  color: var(--store-header-text) !important;
}
body, .font-sans, .store-locator-root {
  font-family: var(--store-font-family) !important;
}
.v-icon, .text-primary {
  color: var(--store-primary) !important;
}
button, .v-btn {
  background: var(--store-button-bg) !important;
  color: var(--store-button-text) !important;
}
.text-gray-700, .text-gray-500, p, label, span, div {
  color: var(--store-body-text);
}
.search-input {
  transition: border-color 0.3s, box-shadow 0.3s, background 0.2s;
  background: #fff;
  border: var(--store-search-border-size) solid var(--store-search-border);
  border-radius: var(--store-search-radius);
  color: var(--store-search-text);
}
.search-input input {
  color: var(--store-search-text) !important;
}
.search-input input::placeholder {
  color: var(--store-search-placeholder) !important;
  opacity: 1;
}
.search-input .v-icon,
.search-input .v-input__icon--prepend .v-icon,
.search-input .v-field__prepend-inner .v-icon {
  color: var(--store-search-icon) !important;
}
.search-input:hover {
  background: #f3f4f6;
  border-color: var(--store-search-border);
}
.search-input:focus {
  border-color: var(--store-primary);
  box-shadow: 0 0 0 2px #e9d8fd;
  background: #fff;
  outline: none;
}

.rotate-90 {
  transform: rotate(90deg);
  transition: transform 0.2s ease;
}

/* Scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Smooth transitions */
.transition-colors {
  transition: background-color 0.2s ease, color 0.2s ease;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .grid.lg\\:grid-cols-3 {
    grid-template-columns: 1fr;
  }
  
  .lg\\:col-span-1 {
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
  }
  
  .max-h-65vh {
    max-height: 40vh;
  }
}

@media (max-width: 768px) {
  .search-input {
    width: 100%;
  }
  
  .grid.md\\:grid-cols-2 {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 900px) {
  .flex-col.md\:flex-row {
    flex-direction: column !important;
  }
  .w-full.md\:w-1\/2 {
    width: 100% !important;
  }
  .h-56.md\:h-48 {
    height: 220px !important;
  }
}

/* Enhanced Mobile Responsiveness */
@media (max-width: 768px) {
  /* Header adjustments */
  .header-bar {
    flex-direction: column !important;
    gap: 1rem !important;
    padding: 1rem !important;
  }
  
  .header-bar > div:first-child {
    width: 100% !important;
    justify-content: center !important;
  }
  
  .header-bar > div:last-child {
    width: 100% !important;
    justify-content: center !important;
    flex-direction: column !important;
    gap: 1rem !important;
  }
  
  .header-tabs {
    order: 1 !important;
    width: 100% !important;
    justify-content: center !important;
    gap: 0.5rem !important;
  }
  
  .header-tab {
    padding: 0.5rem 1rem !important;
    font-size: 0.875rem !important;
    min-width: auto !important;
  }
  
  /* Search input mobile optimization */
  .search-input {
    width: 100% !important;
    max-width: none !important;
  }
  
  /* Brand name and logo adjustments */
  .brand-name-text {
    font-size: 1.125rem !important;
  }
  
  .dynamic-logo {
    max-height: 2rem !important;
  }
  
  /* Main content layout */
  .grid.grid-cols-1.lg\\:grid-cols-3 {
    grid-template-columns: 1fr !important;
  }
  
  .lg\\:col-span-1 {
    order: 2 !important;
    border-right: none !important;
    border-bottom: 1px solid #e5e7eb !important;
    max-height: 50vh !important;
    overflow-y: auto !important;
  }
  
  .lg\\:col-span-2 {
    order: 1 !important;
  }
  
  /* Details panel mobile optimization */
  .details-panel {
    padding: 1rem !important;
    margin-bottom: 1rem !important;
  }
  
  .details-header {
    padding: 1rem !important;
    flex-direction: column !important;
    gap: 1rem !important;
  }
  
  .details-header > div:first-child {
    width: 100% !important;
  }
  
  .details-header > div:last-child {
    width: 100% !important;
    display: flex !important;
    justify-content: center !important;
  }
  
  .details-header-logo {
    max-width: 80px !important;
    max-height: 80px !important;
  }
  
  /* Address panel mobile layout */
  .details-address {
    flex-direction: column !important;
    gap: 1rem !important;
  }
  
  .details-address > div:first-child {
    width: 100% !important;
  }
  
  .details-address > div:last-child {
    width: 100% !important;
    height: 200px !important;
  }
  
  /* Contact panel mobile layout */
  .details-contact {
    flex-direction: column !important;
    gap: 1rem !important;
  }
  
  /* Social links grid */
  .details-social-links .grid {
    grid-template-columns: 1fr !important;
  }
  
  /* Hours grid */
  .details-hours .grid {
    grid-template-columns: 1fr !important;
  }
  
  /* Posts mobile optimization */
  .details-posts .space-y-6 > div {
    padding: 1rem !important;
  }
  
  /* Reviews mobile optimization */
  .review-item {
    padding: 0.75rem !important;
  }
  
  .pagination-container {
    flex-direction: column !important;
    gap: 1rem !important;
    align-items: center !important;
  }
  
  /* Footer mobile optimization */
  .store-footer {
    padding: 1rem 0.5rem !important;
    font-size: 1rem !important;
  }
  
  .footer-links {
    flex-direction: column !important;
    gap: 0.5rem !important;
  }
  
  .footer-separator {
    display: none !important;
  }
  
  .footer-powered-by {
    text-align: center !important;
    margin-top: 0.5rem !important;
  }
  
  /* City filter mobile optimization */
  .city-filter-container {
    padding: 1rem !important;
  }
  
  /* Tree view mobile optimization */
  .side-panel-tree-view {
    padding: 1rem !important;
  }
  
  .location-selector-item {
    padding: 0.75rem !important;
  }
  
  /* Button mobile optimization */
  .details-btn {
    padding: 0.5rem 1rem !important;
    font-size: 0.875rem !important;
  }
}

@media (max-width: 480px) {
  /* Extra small devices */
  .header-bar {
    padding: 0.75rem !important;
  }
  
  .header-tab {
    padding: 0.375rem 0.75rem !important;
    font-size: 0.75rem !important;
  }
  
  .details-panel {
    padding: 0.75rem !important;
  }
  
  .details-header {
    padding: 0.75rem !important;
  }
  
  .details-header h2 {
    font-size: 1.25rem !important;
  }
  
  .details-header-logo {
    max-width: 60px !important;
    max-height: 60px !important;
  }
  
  .brand-name-text {
    font-size: 1rem !important;
  }
  
  .dynamic-logo {
    max-height: 1.75rem !important;
  }
  
  /* Cover photo mobile */
  .cover-photo-container img {
    height: 200px !important;
  }
  
  /* Map mobile */
  .details-address > div:last-child {
    height: 180px !important;
  }
  
  /* Posts images mobile */
  .details-posts img {
    max-height: 200px !important;
  }
  
  /* Footer extra small */
  .store-footer {
    font-size: 0.875rem !important;
    padding: 0.75rem 0.25rem !important;
  }
  
  /* City filter extra small */
  .city-filter-container {
    padding: 0.75rem !important;
  }
  
  .city-filter-dropdown {
    font-size: 0.875rem !important;
  }
}

/* Touch-friendly improvements */
@media (max-width: 1024px) and (hover: none) and (pointer: coarse) {
  /* Increase touch targets */
  .header-tab {
    min-height: 44px !important;
    min-width: 44px !important;
  }
  
  .location-selector-item {
    min-height: 44px !important;
  }
  
  .details-btn {
    min-height: 44px !important;
    min-width: 44px !important;
  }
  
  .footer-link {
    min-height: 44px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  
  /* Improve scrolling */
  .side-panel-tree-view {
    -webkit-overflow-scrolling: touch !important;
  }
  
  /* Better focus states for touch */
  .header-tab:focus,
  .location-selector-item:focus,
  .details-btn:focus,
  .footer-link:focus {
    outline: 2px solid var(--store-primary) !important;
    outline-offset: 2px !important;
  }
}

/* Landscape orientation adjustments */
@media (max-width: 768px) and (orientation: landscape) {
  .lg\\:col-span-1 {
    max-height: 40vh !important;
  }
  
  .details-header {
    flex-direction: row !important;
    align-items: center !important;
  }
  
  .details-header-logo {
    max-width: 60px !important;
    max-height: 60px !important;
  }
  
  .cover-photo-container img {
    height: 150px !important;
  }
  
  .details-address > div:last-child {
    height: 150px !important;
  }
}

.bg-gradient-to-r {
  background: linear-gradient(90deg, #eff6ff 0%, #e0e7ff 100%);
}
.bg-white\/90 {
  background: rgba(255,255,255,0.90);
}
.rounded-2xl {
  border-radius: 1.25rem;
}
.shadow-lg {
  box-shadow: 0 6px 24px 0 rgba(80, 80, 120, 0.12);
}
.shadow-md {
  box-shadow: 0 2px 8px 0 rgba(80, 80, 120, 0.10);
}

/* Ensure flex children in address panel header do not overflow */
.address-header,
.address-header > * {
  max-width: 100%;
}

.location-selector-item {
  background: var(--sidepanel-unselected-bg) !important;
  color: var(--sidepanel-unselected-text) !important;
  border: none !important;
  border-radius: 0 !important;
  transition: background-color 0.2s, color 0.2s;
}
.location-selector-item:not(.location-selected):hover {
  background: var(--sidepanel-hover-bg) !important;
  color: var(--sidepanel-hover-text) !important;
  border: none !important;
  border-radius: 0 !important;
}
.location-selector-item:not(.location-selected):hover p,
.location-selector-item:not(.location-selected):hover span,
.location-selector-item:not(.location-selected):hover div {
  color: var(--sidepanel-hover-text) !important;
}
.location-selector-item:not(.location-selected):hover .v-icon {
  color: var(--sidepanel-hover-text) !important;
}
.location-selected {
  background: var(--sidepanel-selected-bg) !important;
  color: var(--sidepanel-selected-text) !important;
  border: none !important;
  border-radius: 0 !important;
}
.location-selected .v-icon {
  color: var(--sidepanel-selected-text) !important;
}
.location-selected p {
  color: var(--sidepanel-selected-text) !important;
}
.v-icon {
  color: var(--sidepanel-icon) !important;
}
.chevron-icon {
  color: var(--sidepanel-chevron) !important;
}
.current-day {
  background-color: color-mix(in srgb, var(--details-primary) 15%, transparent) !important;
  border-color: var(--details-primary) !important;
}
.dynamic-logo {
  max-height: var(--store-logo-size-mobile) !important;
  max-width: 100%;
  height: auto !important;
  width: auto !important;
  display: block;
}

@media (min-width: 768px) {
  .dynamic-logo {
    max-height: var(--store-logo-size-desktop) !important;
  }
}

.header-bar {
  background: var(--store-header-bg) !important;
}

.header-tabs {
  background: transparent;
}
.header-tab {
  background: var(--store-tab-bg) !important;
  color: var(--store-tab-text) !important;
  border-radius: 0;
  padding: 0.5rem 1.25rem;
  font-weight: 500;
  border: none;
  transition: background 0.2s, color 0.2s;
  cursor: pointer;
}
.header-tab:hover:not(.header-tab-selected) {
  background: var(--store-tab-hover-bg) !important;
  color: var(--store-tab-hover-text) !important;
}
.header-tab-selected {
  background: var(--store-tab-selected-bg) !important;
  color: var(--store-tab-selected-text) !important;
}

/* Ensure icon color matches iconColor for country, state, location, and chevron icons in unselected, not-hovered state */
.side-panel-tree-view .location-selector-item:not(.location-selected):not(:hover) .v-icon.mdi-earth,
.side-panel-tree-view .location-selector-item:not(.location-selected):not(:hover) .v-icon.mdi-map,
.side-panel-tree-view .location-selector-item:not(.location-selected):not(:hover) .v-icon.mdi-store,
.side-panel-tree-view .location-selector-item:not(.location-selected):not(:hover) .chevron-icon {
  color: var(--sidepanel-icon) !important;
}

.details-panel {
  box-shadow: 0 6px 24px 0 rgba(80, 80, 120, 0.12);
  padding: 2rem;
}
.details-address {
  background: var(--details-address-bg) !important;
  color: var(--details-address-text) !important;
  border: none !important;
  border-radius: var(--details-address-radius) !important;
}
.details-contact {
  background: var(--details-contact-bg) !important;
  color: var(--details-contact-text) !important;
  border: none !important;
  border-radius: var(--details-contact-radius) !important;
}
.details-description {
  background: var(--details-description-bg) !important;
  color: var(--details-description-text) !important;
  border: none !important;
  border-radius: var(--details-description-radius) !important;
}
.details-cover-photo {
  background: var(--details-cover-photo-bg) !important;
  color: var(--details-cover-photo-text) !important;
  border: none !important;
  border-radius: var(--details-cover-photo-radius) !important;
}
.details-social-links {
  background: var(--details-social-links-bg) !important;
  color: var(--details-social-links-text) !important;
  border: none !important;
  border-radius: var(--details-social-links-radius) !important;
}
.details-hours {
  background: var(--details-hours-bg) !important;
  color: var(--details-hours-text) !important;
  border: none !important;
  border-radius: var(--details-hours-radius) !important;
}
.details-categories {
  background: var(--details-categories-bg) !important;
  color: var(--details-categories-text) !important;
  border: none !important;
  border-radius: var(--details-categories-radius) !important;
}
.details-products {
  background: var(--details-products-bg) !important;
  color: var(--details-products-text) !important;
  border: none !important;
  border-radius: var(--details-products-radius) !important;
}
.details-posts {
  background: var(--details-posts-bg) !important;
  color: var(--details-posts-text) !important;
  border: none !important;
  border-radius: var(--details-posts-radius) !important;
}
.details-reviews {
  background: var(--details-reviews-bg) !important;
  color: var(--details-reviews-text) !important;
  border: none !important;
  border-radius: var(--details-reviews-radius) !important;
}
.details-actions {
  background: var(--details-actions-bg) !important;
  color: var(--details-actions-text) !important;
  border: none !important;
  border-radius: var(--details-actions-radius) !important;
}
.details-btn {
  color: var(--details-btn-text) !important;
  background: var(--details-btn-bg) !important;
  border: none !important;
  border-radius: var(--details-btn-radius) !important;
  transition: background 0.2s, color 0.2s;
}
.details-btn .v-icon {
  color: var(--details-btn-text) !important;
}
.details-btn:hover:not(.selected) {
  color: var(--details-btn-hover-text) !important;
  background: var(--details-btn-hover-bg) !important;
  border: none !important;
  border-radius: var(--details-btn-hover-border-radius) !important;
}
.details-btn:hover:not(.selected) .v-icon {
  color: var(--details-btn-hover-text) !important;
}
.details-btn.selected {
  color: var(--details-btn-selected-text) !important;
  background: var(--details-btn-selected-bg) !important;
  border: none !important;
  border-radius: var(--details-btn-selected-border-radius) !important;
}
.details-btn.selected .v-icon {
  color: var(--details-btn-selected-text) !important;
}

.details-header {
  background: var(--details-header-bg) !important;
  color: var(--details-header-text) !important;
  border: none !important;
  border-radius: var(--details-header-radius) !important;
}

.details-header-icon {
  color: var(--details-primary) !important;
}

.category-badge {
  background: var(--details-primary) !important;
  color: var(--details-header-text) !important;
}
.store-footer {
  border-top: 6px solid #000;
  padding: 18px 0 10px 0;
  background: #fff;
  text-align: center;
  font-size: 18px;
  font-family: serif;
  margin-top: 0;
  color: #000;
}
.footer-links {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
  gap: 0.5em;
  flex-direction: row;
}
.footer-link {
  color: #000;
  text-decoration: none;
  margin: 0 2px;
  white-space: nowrap;
  cursor: pointer;
}
.footer-link:hover {
  text-decoration: underline;
  color: #000;
}
.footer-separator {
  color: #000;
}

.footer-powered-by {
  text-align: center;
  margin-top: 0.5rem;
}

.footer-powered-by a {
  color: inherit;
  text-decoration: none;
}

.footer-powered-by a:hover {
  text-decoration: underline;
}
.city-filter-label {
  color: #000;
  letter-spacing: 0.01em;
}
.city-filter-dropdown {
  border: 2px solid #000;
  background: #fff;
  color: #000;
  font-weight: 500;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-shadow: 0 1px 4px 0 rgba(23, 115, 234, 0.05);
}
.city-filter-dropdown:focus {
  border-color: #000;
  box-shadow: 0 0 0 2px color-mix(in srgb, #000 30%, transparent);
}
.city-filter-dropdown option {
  color: #222;
}

.city-filter-container {

  background: #fff;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px 0 rgba(80, 80, 120, 0.08);
  padding-top: 1.25rem;
  z-index: 2;
  position: relative;
  width: 100%;
}

.status-badge {
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.status-badge-deleted {
  background-color: #fee2e2;
  color: #dc2626;
  border: 1px solid #fecaca;
}
.status-badge-draft {
  background-color: #fef3c7;
  color: #d97706;
  border: 1px solid #fed7aa;
}
.status-badge-scheduled {
  background-color: #dbeafe;
  color: #2563eb;
  border: 1px solid #bfdbfe;
}
.status-badge-published {
  background-color: #dcfce7;
  color: #16a34a;
  border: 1px solid #bbf7d0;
}

/* Cover Photo Styles */
.cover-photo-container {
  position: relative;
  overflow: hidden;
  border-radius: 0.75rem;
}

.cover-photo-container img {
  transition: transform 0.3s ease;
}

.cover-photo-container:hover img {
  transform: scale(1.02);
}

/* Social Links Styles */
.details-social-links a {
  text-decoration: none;
  transition: all 0.2s ease;
}

.details-social-links a:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.details-social-links .v-icon {
  flex-shrink: 0;
}

/* Pagination Styles */
.details-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #e5e7eb !important;
  color: #6b7280 !important;
  border: none !important;
}

.details-btn:disabled:hover {
  background: #e5e7eb !important;
  color: #6b7280 !important;
  border: none !important;
}

.details-btn:disabled .v-icon {
  color: #6b7280 !important;
}

/* Logo in details header card */
.details-header-logo {
  max-width: 150px;
  max-height: 150px;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px 0 rgba(80, 80, 120, 0.10);
  background: #fff;
}

.brand-name-text {
  font-size: var(--store-brand-name-size) !important;
  color: var(--store-brand-name-color) !important;
}

/* Review Styles */
.review-item {
  background: rgba(255, 255, 255, 0.8) !important;
  border: 1px solid rgba(23, 115, 234, 0.1) !important;
  border-radius: 0.75rem !important;
  padding: 1rem !important;
  box-shadow: 0 2px 8px 0 rgba(80, 80, 120, 0.08) !important;
  transition: all 0.2s ease !important;
}

.review-item:hover {
  box-shadow: 0 4px 12px 0 rgba(80, 80, 120, 0.12) !important;
  transform: translateY(-1px) !important;
}

.review-author {
  font-weight: 600 !important;
  color: var(--details-reviews-text) !important;
  font-size: 0.875rem !important;
}

.review-date {
  font-size: 0.75rem !important;
  color: var(--details-secondary) !important;
}

.review-comment {
  color: var(--details-reviews-text) !important;
  font-size: 0.875rem !important;
  line-height: 1.5 !important;
}

.pagination-container {
  display: flex !important;
  align-items: center !important;
  justify-content: space-between !important;
  margin-top: 1.5rem !important;
  padding-top: 1rem !important;
  border-top: 1px solid rgba(23, 115, 234, 0.1) !important;
}

.pagination-info {
  font-size: 0.875rem !important;
  color: var(--details-secondary) !important;
}

.pagination-page-info {
  font-size: 0.875rem !important;
  color: var(--details-secondary) !important;
  padding: 0 0.5rem !important;
}

/* Skeleton Loader Styles */
@keyframes skeleton-pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

.skeleton-label {
  height: 16px;
  width: 80px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-dropdown {
  height: 40px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-tree-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
}

.skeleton-chevron {
  width: 16px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 2px;
}

.skeleton-icon {
  width: 16px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 50%;
}

.skeleton-text {
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-count {
  width: 40px;
  height: 16px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-location-item {
  height: 60px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 12px;
  margin-bottom: 8px;
}

.skeleton-cover-photo {
  height: 256px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 12px;
}

.skeleton-details-header {
  height: 120px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 12px;
}

.skeleton-panel {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px 0 rgba(80, 80, 120, 0.08);
}

.skeleton-panel-header {
  height: 24px;
  width: 200px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 16px;
}

.skeleton-panel-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.skeleton-text-long {
  height: 20px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-text-medium {
  height: 16px;
  width: 70%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-button {
  height: 40px;
  width: 140px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-map {
  height: 192px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 12px;
}

.skeleton-contact-item {
  height: 40px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-review-item {
  height: 80px;
  width: 100%;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

/* Header Skeleton Styles */
.skeleton-logo {
  width: 48px;
  height: 48px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

.skeleton-brand-name {
  height: 24px;
  width: 120px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-location-count {
  height: 16px;
  width: 80px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 4px;
}

.skeleton-tab {
  height: 36px;
  width: 80px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 6px;
}

.skeleton-search {
  height: 40px;
  width: 320px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200px 100%;
  animation: skeleton-shimmer 1.5s infinite;
  border-radius: 8px;
}

/* Responsive skeleton adjustments */
@media (max-width: 1024px) {
  .skeleton-panel {
    padding: 16px;
  }
  
  .skeleton-cover-photo {
    height: 200px;
  }
  
  .skeleton-details-header {
    height: 100px;
  }
}

@media (max-width: 768px) {
  .skeleton-panel {
    padding: 12px;
  }
  
  .skeleton-cover-photo {
    height: 180px;
  }
  
  .skeleton-details-header {
    height: 80px;
  }
  
  .skeleton-map {
    height: 160px;
  }
  
  .skeleton-search {
    width: 200px;
  }
  
  .skeleton-brand-name {
    width: 80px;
  }
  
  .skeleton-location-count {
    width: 60px;
  }
  
  .skeleton-tab {
    width: 60px;
  }
}

@media (min-width: 769px) {
  .header-tabs {
    justify-content: flex-end !important;
    width: auto !important;
  }
}
@media (max-width: 768px) {
  .header-tabs {
    justify-content: center !important;
    width: 100% !important;
  }
}

/* Mobile side panel overlay */
.mobile-side-panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: flex;
  align-items: flex-start;
  animation: fadeIn 0.3s ease-out;
}

.mobile-side-panel {
  background-color: white;
  width: 85%;
  max-width: 320px;
  height: 100%;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.15);
  animation: slideInLeft 0.3s ease-out;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.mobile-side-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
  position: relative;
}

.mobile-side-panel-title {
  display: flex;
  align-items: center;
  flex: 1;
  justify-content: center;
  margin-left: 20px;
}

.mobile-side-panel-close {
  background: transparent !important;
  border: none !important;
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  color: #6b7280 !important;
  transition: all 0.2s;
}

.mobile-side-panel-close:hover {
  background-color: transparent !important;
  color: #374151 !important;
}

.mobile-side-panel-close .v-icon {
  color: inherit !important;
}

.mobile-side-panel-filter {
  padding: 16px 20px;
  border-bottom: 1px solid #e5e7eb;
  background-color: white;
}

.mobile-side-panel-city-dropdown {
  background-color: white;
  color: #374151;
  font-weight: 500;
  font-size: 1rem;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.mobile-side-panel-city-dropdown:focus {
  border-color: var(--store-primary);
  box-shadow: 0 0 0 3px rgba(23, 115, 234, 0.1);
}

.mobile-side-panel-list {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  background-color: white;
}

.mobile-side-panel-country-section {
  margin-bottom: 16px;
}

.mobile-side-panel-country-header {
  display: flex;
  align-items: center;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-bottom: 8px;
}

.mobile-side-panel-country-header:hover {
  background-color: #f3f4f6;
}

.mobile-side-panel-state-section {
  margin-left: 16px;
}

.mobile-side-panel-state-item {
  margin-bottom: 8px;
}

.mobile-side-panel-state-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  background-color: #f3f4f6;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-bottom: 4px;
}

.mobile-side-panel-state-header:hover {
  background-color: #e5e7eb;
}

.mobile-side-panel-location-section {
  margin-left: 16px;
}

.mobile-side-panel-location-item {
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 4px;
  background-color: white;
  border: 1px solid #e5e7eb;
}

.mobile-side-panel-location-item:hover {
  background-color: #f9fafb;
  border-color: #d1d5db;
}

.mobile-side-panel-location-selected {
  background-color: var(--sidepanel-selected-bg) !important;
  border-color: var(--sidepanel-selected-border) !important;
  color: var(--sidepanel-selected-text) !important;
}

.mobile-side-panel-location-selected .v-icon {
  color: var(--sidepanel-selected-text) !important;
}

.mobile-side-panel-location-selected p {
  color: var(--sidepanel-selected-text) !important;
}

/* Mobile hamburger button */
.mobile-hamburger-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  transition: all 0.2s;
  white-space: nowrap;
  min-height: 36px;
}

@media (max-width: 480px) {
  .mobile-hamburger-btn {
    padding: 5px 8px;
    font-size: 0.8rem;
    gap: 4px;
  }
  
  .mobile-hamburger-btn .v-icon {
    font-size: 18px;
  }
}

/* Fixed left menu button for small desktop */
.fixed-left-menu-btn {
  position: fixed;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--store-primary);
  color: white;
  border: none;
  border-radius: 50px;
  padding: 12px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 999;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 60px;
  min-height: 60px;
}

.fixed-left-menu-btn:hover {
  background: var(--store-btn-hover-bg, #125bb5);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  transform: translateY(-50%) scale(1.05);
}

.fixed-left-menu-btn:active {
  transform: translateY(-50%) scale(0.95);
}

.fixed-left-menu-btn .v-icon {
  font-size: 24px;
}

.fixed-left-menu-btn .menu-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Responsive adjustments for fixed menu button */
@media (max-width: 768px) {
  .fixed-left-menu-btn {
    display: none;
  }
}

@media (max-width: 1024px) and (min-width: 769px) {
  .fixed-left-menu-btn {
    left: 15px;
    padding: 10px 14px;
    min-width: 56px;
    min-height: 56px;
  }
  
  .fixed-left-menu-btn .v-icon {
    font-size: 20px;
  }
  
  .fixed-left-menu-btn .menu-label {
    font-size: 0.7rem;
  }
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    transform: translateX(-100%);
  }
  to {
    transform: translateX(0);
  }
}

/* Responsive adjustments for mobile side panel */
@media (max-width: 480px) {
  .mobile-side-panel {
    width: 90%;
    max-width: 280px;
  }
  
  .mobile-side-panel-header {
    padding: 12px 16px;
  }
  
  .mobile-side-panel-filter {
    padding: 12px 16px;
  }
  
  .mobile-side-panel-list {
    padding: 12px 16px;
  }
  
  .mobile-side-panel-title {
    font-size: 1rem;
  }
  
  .mobile-side-panel-title .v-icon {
    font-size: 20px;
  }
}

/* Extra small screen adjustments */
@media (max-width: 360px) {
  .mobile-side-panel {
    width: 95%;
    max-width: 260px;
  }
  
  .mobile-side-panel-header {
    padding: 10px 12px;
  }
  
  .mobile-side-panel-filter {
    padding: 10px 12px;
  }
  
  .mobile-side-panel-list {
    padding: 10px 12px;
  }
  
  .mobile-side-panel-location-item {
    padding: 10px;
  }
  
  .mobile-side-panel-country-header {
    padding: 10px;
  }
  
  .mobile-side-panel-state-header {
    padding: 8px 10px;
  }
}

/* Landscape orientation adjustments */
@media (max-width: 1024px) and (orientation: landscape) {
  .mobile-side-panel {
    width: 70%;
    max-width: 300px;
  }
}

/* Touch-friendly improvements for mobile side panel */
@media (max-width: 1024px) and (hover: none) and (pointer: coarse) {
  .mobile-side-panel-location-item {
    min-height: 48px;
  }
  
  .mobile-side-panel-country-header {
    min-height: 48px;
  }
  
  .mobile-side-panel-state-header {
    min-height: 44px;
  }
  
  .mobile-side-panel-close {
    min-height: 44px;
    min-width: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
}

/* Mobile side panel search */
.mobile-side-panel-search {
  margin-bottom: 16px;
}

.mobile-side-panel-search .search-input {
  width: 100%;
  max-width: none;
}

/* Post Grid Styles */
.post-grid {
  align-items: start !important;
}

.post-card {
  height: fit-content !important;
  align-self: start !important;
}

.post-content {
  align-items: flex-start !important;
  justify-content: flex-start !important;
}
</style> 